import { formatConversationValue } from "../lib/conversationContent.js";
import { Fragment, createContext, forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useDetailHeaderSlot } from "./detailHeaderSlot.js";
import { readUrlParam, readUrlParams, writeUrlParam } from "../lib/urlParams.js";
import { findScrollParent, landOnRow, READER_TAKEOVER_EVENTS, stickyLandingInset } from "../lib/scrollParent.js";
import { formatCompact, formatUsd } from "../lib/renderers.js";
import { tokenGrainMeta } from "../lib/tokenGrain.js";
import { providerCacheApplies, contextOf, cacheReuseRate } from "../lib/tokenVolume.js";
import { estimateTokenCost } from "../lib/tokenCost.js";
import { fullTimestampFormatter } from "../lib/traceUtils.js";
import { buildTurnStamps, toolStampsByUseId, toolStampOf, toolStamp } from "../lib/traceTurnStamp.js";
import { StampCaptureContext, TraceStamp } from "./TraceStamp.jsx";
// One reading of a tool_result on both sides of the Context/Lanes pivot: this
// was a byte-identical second copy, which is how the two tabs would have started
// disagreeing about the same block.
import { toolResultText } from "../lib/traceToolCall.js";
import { unwrapCodeModeTurns, cellEnvelopeNote, codeModeReadable } from "../lib/traceCodeModeCells.js";
import { useGrammarWarm } from "../lib/useGrammarWarm.js";
import { deriveAgentIdentity } from "../lib/agentIdentity.js";
import { confidenceBand, credscanTipLines } from "../lib/credscanTip.js";
import { extractAllTraceFacets } from "../lib/dimAdapters.js";
import { useLabel, useDimensionEntry, encodeScopePath } from "../lib/dimensionDefs.js";
import { browserFamilyLabel, deriveTraceTooling } from "../lib/traceTooling.js";
import { classifyHarnessSegments } from "../lib/harnessTemplates.js";
import { parseCodexUserWrapper } from "../lib/codexUserWrappers.js";
import { signalDefByCommand } from "../lib/signalCatalog.js";
import { useTraceSignalHighlights } from "../lib/useTraceSignalHighlights.js";
import { buildHighlightMap, markRanges, markEntryAt, isInvisibleRun } from "../lib/signalHighlight.js";
import { takeJoinFollow, scanTurnsForNeedles } from "../lib/joinFollow.js";
import {
  SEARCH_KINDS, SEARCH_MARK_TYPE, blocksOfKinds, hitsInDisplayOrder, kindCounts, searchHighlightMap, searchTurns, toolDefText, toolResultReading, toolUseBodyText, turnsOfKinds,
} from "../lib/traceSearch.js";
import TraceSearchBar from "./TraceSearchBar.jsx";
import { loadHarnessProfile } from "../lib/harnessProfile.js";
import { traceCaptureSourceEnabled } from "../lib/featureFlags.js";
import { computeWindow, windowsEqual, topVisibleIndex } from "../lib/virtualWindow.js";
import {
  TURN_FOCUS_PARAM, clampTurnFocus, formatTurnFocus, landingMayReadEarlier, parseTurnFocus, parseTurnFocusMessage,
} from "../lib/traceTurnFocus.js";
import { FINDING_FOCUS_PARAM, parseFindingFocus } from "../lib/traceReviewFindings.js";
import { GRAPH_CLAIM_PARAM, GRAPH_FOCUS_PARAM, parseGraphClaimFocus, parseGraphFocus } from "../lib/traceGraphOverlay.js";
import { activateGraphChat, sendGraphReply } from "../lib/graphReplies.js";
import { query } from "../lib/dataClient.js";
import { extractSpawnPrompts, buildLanesThreads } from "../lib/traceThreadsWiring.js";
import { CONTEXT_EYEBROW_CLASS } from "./SplitPage.jsx";
import ControlGroup from "./ControlGroup.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import ScrollArea from "./ScrollArea.jsx";
import ContrastPanel from "./ContrastPanel.jsx";
import WorkTabs from "./WorkTabs.jsx";
import TraceOverlays from "./TraceOverlays.jsx";
import TraceWorkFeed from "./TraceWorkFeed.jsx";
import TraceLanesView from "./TraceLanesView.jsx";
import WorkspaceCardPreview from "./WorkspaceCardPreview.jsx";
import TraceDetailSkeleton from "./TraceDetailSkeleton.jsx";
import ReadingSwitcherGroup from "./ReadingSwitcherGroup.jsx";
import { previewTraceCanvas } from "../lib/traceCanvasStore.js";
import { AnswerGapMarker } from "./TraceRow.jsx";
import { answerGapForTurn, answerGapsByEvent, isOverdue, overdueAnswerCount, turnAnswerGapText } from "../lib/copilotAnswerGaps.js";

// Shown while the JavaScript grammar loads on a trace that has code-mode
// cells to read. It exists because there is only ONE reader: a second, weaker
// parser filling this moment would make what the trace says depend on how
// recently the tab was opened. The wait is ~58ms of init plus a 128KB gzipped
// fetch, once per session.
function CodeModePending() {
  return (
    <div
      aria-busy="true"
      data-code-mode-pending=""
      className="grid min-h-[120px] place-items-center px-[12px] py-[24px] text-[11px] leading-[1.3] text-[color:var(--muted)]"
    >
      Reading this trace&apos;s code cells...
    </div>
  );
}

// The load failed and parserHost latched it for the session, so no wait will
// ever finish. The cells render wrapped - plain exec calls, the state every
// cell was in before this reading existed - and this row says so once. Quiet
// on purpose: the reading below is real, just not unwrapped.
function CodeModeUnavailable() {
  const text = "This trace's code cells could not be read. Showing them as written.";
  return (
    <div
      role="status"
      data-code-mode-unavailable=""
      className="rounded-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[6px] text-[11px] leading-[1.3] text-[color:var(--muted)]"
    >
      {text}
    </div>
  );
}

// The same caveat for the Lanes reading, where it rides the axis head's hover.
const CODE_CELLS_AS_WRITTEN = "Code cells are shown as written: the JavaScript grammar did not load in this browser session, so the work inside this trace's code cells is not unwrapped onto the lanes.";

const CONTEXT_META_CLASS = "text-[11px] leading-[1.24] text-[color:var(--muted)]";
const CONTEXT_VALUE_CLASS = "text-[13px] leading-[1.24] text-[color:var(--ink)]";

const CONTEXT_TAB_OPTIONS = [
  ["attribution", "Attribution"],
  ["request", "Request"],
];

function getOverlayViewport(node) {
  return (
    node?.querySelector("[data-overlayscrollbars-viewport]") ||
    node?.querySelector(".os-viewport") ||
    node ||
    null
  );
}

// Drop redundant leading slashes / cwd noise so pills don't all share a
// long common prefix that pushes the distinguishing tail off the right
// edge. Pure display — onClick still navigates with the full path so
// the dim browser resolves the canonical id.
function shortenPath(path) {
  if (!path || typeof path !== "string") return path;
  // Split on either slash form so Windows paths (`C:\foo\bar\baz.txt`)
  // shorten the same way POSIX paths do. Re-join with whichever
  // separator the original used so the truncated form still looks
  // native to the platform that produced it.
  const segments = path.split(/[/\\]/).filter(Boolean);
  if (segments.length <= 3) return path;
  const sep = path.includes("\\") && !path.includes("/") ? "\\" : "/";
  return `…${sep}${segments.slice(-3).join(sep)}`;
}


function DetailRow({ label, value }) {
  return (
    <div className="grid gap-[1px]">
      <span className={CONTEXT_META_CLASS}>{label}</span>
      <span className={CONTEXT_VALUE_CLASS}>{value}</span>
    </div>
  );
}

const DIM_LABELS = {
  files: "File",
  hosts: "Host",
  skills: "Skill",
  sessions: "Trace",
  clusters: "Topic",
  agents: "Agent",
  endpoints: "Endpoint",
  commands: "Command",
  urls: "URL",
  tools: "Tool",
  models: "Model",
};

export function ScopeChip({ dim, label, onClick, title, bare = false }) {
  const tag = DIM_LABELS[dim] || dim;
  const customTip = title && title !== label ? title : undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-w-0 min-h-[22px] max-w-full items-center gap-[5px] rounded-[6px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] text-[11px] leading-[1.08] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer"
    >
      {/* bare: the dim tag is omitted where position already names the kind
          (the identity breadcrumb) — the tags are what forced its chips down
          to one-character stubs at panel width. */}
      {!bare && <span className="shrink-0 text-[11px] text-[color:var(--muted-soft)]">{tag}</span>}
      {/* The house tooltip, not a native title=: shows the full value when
          the chip truncates it (always, when a caller supplies richer text). */}
      <OverflowTooltip text={label} tooltipContent={customTip} always={Boolean(customTip)} className="min-w-0" />
    </button>
  );
}

// ── Block-level rendering ───────────────────────────────────────────
//
// Each Anthropic content block (text / tool_use / tool_result /
// thinking / image / unknown) renders as its own labeled mini-panel
// within the parent turn. Facets attach directly to the originating
// tool_use block — not mirrored onto the matching tool_result — so a
// chip's source is visibly the input.command (or input.file_path, etc.)
// shown in the same panel. tool_result blocks display the back-link
// (tool_use_id + linked tool name) so a reader can trace a result to
// its call without duplicating chips.

const BLOCK_EYEBROW_BASE = "text-[12px] leading-[1.08] font-medium";

function getXmlParts(text) {
  if (!text) return null;
  const xmlTagPattern = /(<\/?[\w-]+(?:\s+[\w-]+="[^"]*")*\s*\/?>)/g;
  if (!xmlTagPattern.test(text)) return null;
  return text.split(/(<\/?[\w-]+(?:\s+[\w-]+="[^"]*")*\s*\/?>)/g);
}

function FormattedText({ text }) {
  const parts = getXmlParts(text);
  if (!parts) return <>{text}</>;
  const tagRe = /^<\/?[\w-]+(?:\s+[\w-]+="[^"]*")*\s*\/?>$/;
  return (
    <>
      {parts.map((part, i) =>
        tagRe.test(part)
          ? <span key={i} className="text-[color:var(--muted)]">{part}</span>
          : <span key={i}>{part}</span>
      )}
    </>
  );
}

// Harness x-ray overlay: when the request detail's Harness toggle is
// on, this carries the scope's mined scaffolding key set
// (lib/harnessProfile) down to every text block. Null when off.
const HarnessOverlayContext = createContext(null);

// Signal highlight overlay: a Map<"turnIndex:blockIndex", [{match, type}]>
// (built by signalHighlight.buildHighlightMap from the trace's fired signals)
// carried down so each text/tool-result block can mark exactly the span that
// fired a signal. Null when the trace has no fired signals. Exported for the
// block-highlight render test (drives TurnBlock with a synthetic map).
export const SignalHighlightContext = createContext(null);
// The find-in-trace hit the reader is on, by hit id, so its mark reads as current.
export const SearchCurrentContext = createContext(null);
// Recorded tool-call instants keyed by the provider's tool_use id, so a
// `tool_use` block deep in the block tree can be dated without threading a
// prop through every turn and block renderer.
export const ToolStampContext = createContext(null);

// Accent for a signal mark, by tag-namespace prefix (mirrors TraceRow's badge
// accents): risk reads hot, pii warm, audit muted.
const MARK_ACCENT = {
  risk: "var(--red,#c0392b)",
  pii: "var(--amber,#b8860b)",
  audit: "var(--muted)",
  // Experimental cost-efficiency marks — teal, distinct from the security
  // namespaces and ranked below them in the locator.
  efficiency: "var(--cyan,#2ea9b8)",
  // Persisted review citations: evidence spans the
  // judge quoted, verified against the trace before they reach this map.
  eval: "var(--amber,#b8860b)",
  // Hand-off follow-through (lanes content joins) -- the shared strings the
  // reader crossed a session boundary to see, in the join surface's own color.
  join: "var(--join, #5db2ff)",
  // Find-in-trace hits. Signal marks stand down while a search is on, so the
  // two never share a screen.
  search: "var(--amber,#b8860b)",
};

// Tooltip text for a cost-efficiency mark: the check id + why it's waste. Keyed
// by the `rule` (check id) the signal emits. Kept in sync with the checks in
// tokenEfficiencyTrigger (exampleTriggers.js) by name.
const EFFICIENCY_TIP = new Map([
  ["redundant-read", "redundant-read: this unchanged file was already in context. Reading it again uses more input tokens."],
  ["read-after-write", "read-after-write: this file was just changed, so its current content is already in context. Reading it again uses more input tokens."],
  ["unbounded-large-read", "unbounded-large-read: this large file was read without a line range. Use a targeted range or search to reduce input tokens."],
]);

// Tooltip for ANY signal mark: name (and, where known, describe) the signal that
// fired on this span. A mark entry carries `command` (the fired signal) and, for
// the client-only efficiency signal, the specific `rule` (check id). Efficiency
// gets the rich check text; every other signal resolves its command to the
// catalog's title + description. Falls back to the raw `type` so a mark is never
// anonymous.
export function markTip(entry) {
  if (!entry || entry.type === SEARCH_MARK_TYPE) return null;
  if (entry.rule && EFFICIENCY_TIP.has(entry.rule)) return EFFICIENCY_TIP.get(entry.rule);
  if (entry.command === "join.follow") {
    return "Shared content: this span also appears in the trace you followed the hand-off from.";
  }
  // The finding's own text (loc.detail) — for an invisible-run mark this is the
  // only place the decoded payload becomes readable. When a mark carries one,
  // the tooltip is the signal's NAME plus the finding: the concrete finding
  // says more than the catalog description, but the name stays so a mark is
  // never anonymous. Plain text by construction: HoverTooltip renders the
  // label as a string, never markup.
  const detail = typeof entry.detail === "string" && entry.detail ? entry.detail : null;
  let tip = null;
  if (entry.command === "credential-material" && typeof entry.type === "string") {
    // Credscan says what was found and how sure the classifier is, in the
    // reader's words rather than the pipeline's stage names. The second line
    // needs a <br> because the tooltip renders with collapsing whitespace, so
    // a newline character would not break the line.
    const { title, detail: scoreLine } = credscanTipLines(entry);
    if (!scoreLine) return title;
    return (<>{title}<br />{scoreLine}</>);
  }
  if (entry.command) {
    const def = signalDefByCommand(entry.command);
    if (def) {
      if (detail) tip = `${def.label}. Found: ${detail}`;
      else tip = def.description ? `${def.label}: ${def.description}` : def.label;
    }
  }
  // Last resort: the mark's own type, humanized (e.g. "risk.reverse_shell").
  if (!tip && typeof entry.type === "string") {
    const tag = entry.type.includes(".") ? entry.type.slice(entry.type.indexOf(".") + 1) : entry.type;
    const name = tag.replace(/_/g, " ");
    tip = detail ? `${name}. Found: ${detail}` : name;
  }
  // Any other signal that grows a classifier score gets the same banded
  // wording as credscan, so one score never reads two ways across the panel.
  const band = confidenceBand(entry.confidence);
  if (band) {
    const score = `${band} confidence (${Math.round(entry.confidence * 100)}%)`;
    return tip ? `${tip} ${score}` : score;
  }
  return tip;
}
function markAccent(type) {
  const ns = typeof type === "string" ? type.split(".")[0] : "";
  return MARK_ACCENT[ns] || "var(--green)";
}

// Render `text` with the signal-matched spans wrapped in a highlight mark
// (background tint + underline in the signal's accent), unmarked runs still
// XML-formatted. Used by CollapsibleText when a block carries signal matches.
function HighlightedText({ text, matches }) {
  const currentHit = useContext(SearchCurrentContext);
  const ranges = markRanges(text || "", matches);
  if (!ranges.length) return <FormattedText text={text || ""} />;
  const out = [];
  let cursor = 0;
  ranges.forEach(([start, end, type], i) => {
    if (start > cursor) out.push(<FormattedText key={`p${i}`} text={text.slice(cursor, start)} />);
    const accent = markAccent(type);
    const ns = typeof type === "string" ? type.split(".")[0] : "";
    // Tooltip names the signal that fired on this span. The range carries only
    // (start,end,type); recover the full mark entry (which also has command/rule)
    // by matching type + placement against the block's marks, deciding placement
    // the same way markRanges did so a re-serialized block keeps its tooltip.
    const entry = markEntryAt(matches, text, start, type);
    const tip = markTip(entry);
    const span = text.slice(start, end);
    // A span of only invisible code points (a unicode-smuggling carrier run)
    // renders as a zero-width mark nothing can see. Show a visible placeholder
    // instead; the tooltip carries the finding (markTip appends entry.detail).
    // The raw carriers are deliberately not rendered inside the mark, so
    // selecting the placeholder copies its label rather than re-smuggling the
    // hidden payload.
    const hidden = isInvisibleRun(span);
    const hiddenCount = hidden ? Array.from(span).length : 0;
    const hit = entry?.type === SEARCH_MARK_TYPE ? entry.hit : null;
    const current = hit != null && hit === currentHit;
    const markEl = (
      <mark
        key={`m${i}`}
        data-signal-mark=""
        data-signal-mark-hidden={hidden ? "" : undefined}
        data-accent={ns}
        data-search-hit={hit ?? undefined}
        data-search-current={current ? "" : undefined}
        className="rounded-[1px] text-[color:var(--ink)]"
        style={{
          backgroundColor: `color-mix(in srgb, ${accent} ${current ? 55 : 22}%, transparent)`,
          boxShadow: current ? `inset 0 -2px 0 ${accent}, 0 0 0 1px ${accent}` : `inset 0 -2px 0 ${accent}`,
          WebkitBoxDecorationBreak: "clone",
          boxDecorationBreak: "clone",
          ...(hidden ? { fontStyle: "italic" } : {}),
        }}
      >
        {hidden
          ? `[${hiddenCount} hidden character${hiddenCount === 1 ? "" : "s"}]`
          : span}
      </mark>
    );
    out.push(
      tip
        ? <HoverTooltip key={`m${i}`} placement="top" wide label={tip}>{markEl}</HoverTooltip>
        : markEl,
    );
    cursor = end;
  });
  if (cursor < text.length) out.push(<FormattedText key="tail" text={text.slice(cursor)} />);
  return <>{out}</>;
}

// Collapsible pre block. Used for any block whose body is a long string
// (text / tool_result / thinking). Auto-clamps to 5 lines and only
// renders the Expand control when content actually overflows.
// `role` is the owning turn's role — when the harness overlay is
// active, input-side blocks (system/user — the channels the miner
// classifies) render scaffolding lines dimmed.
function CollapsibleText({ text, defaultOpen = false, role = null, turnIndex = null, blockIndex = null }) {
  const ref = useRef(null);
  const [clamped, setClamped] = useState(false);
  const overlay = useContext(HarnessOverlayContext);
  // Signal marks for THIS block (if any signal fired here). Takes visual
  // precedence over the harness dimming — a fired signal is the thing to surface.
  const highlightMap = useContext(SignalHighlightContext);
  const marks = highlightMap && turnIndex != null && blockIndex != null
    ? highlightMap.get(`${turnIndex}:${blockIndex}`)
    : null;
  // A matched block opens un-clamped so the highlighted span is never hidden
  // below the 5-line fold — the whole point is to SHOW where the signal fired.
  const hasMarks = !!(marks && marks.length);
  const [open, setOpen] = useState(defaultOpen || hasMarks);
  useEffect(() => {
    if (hasMarks) setOpen(true);
  }, [hasMarks]);
  const segments = useMemo(() => {
    if (!overlay?.boilerKeys?.size) return null;
    if (role !== "system" && role !== "user") return null;
    const segs = classifyHarnessSegments(text || "", overlay.boilerKeys);
    return segs.some((s) => s.boiler) ? segs : null;
  }, [overlay, role, text]);
  useEffect(() => {
    const el = ref.current;
    if (!el || open) { setClamped(false); return; }
    const check = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    const id = requestAnimationFrame(check);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { cancelAnimationFrame(id); ro.disconnect(); };
  }, [open, text]);
  return (
    <div className="grid gap-[4px] min-w-0">
      <pre
        ref={ref}
        className={[
          "m-0 max-w-full whitespace-pre-wrap [overflow-wrap:anywhere] [word-break:break-word] font-mono text-[12px] leading-[1.52] text-[color:var(--ink)]",
          !open ? "line-clamp-5" : "",
        ].join(" ")}
      >
        {marks && marks.length ? (
          <HighlightedText text={text || ""} matches={marks} />
        ) : segments ? (
          segments.map((seg, i) =>
            seg.boiler ? (
              // Scaffolding reads as a SHADED STRIP, not just dimmer
              // glyphs — block text is already muted-toned in places
              // (tool results, tag tints), so color alone disappears.
              // box-decoration-break: clone makes every wrapped line of
              // the span carry its own background + left rail, so a
              // multi-line template renders as one continuous shaded
              // region with a rail down its left edge.
              <span
                key={i}
                style={{
                  color: "var(--muted-soft)",
                  backgroundColor: "var(--line-soft)",
                  boxShadow: "inset 2px 0 0 var(--line-strong)",
                  paddingLeft: "7px",
                  WebkitBoxDecorationBreak: "clone",
                  boxDecorationBreak: "clone",
                }}
              >
                {seg.text}
              </span>
            ) : (
              <FormattedText key={i} text={seg.text} />
            ),
          )
        ) : (
          <FormattedText text={text || ""} />
        )}
      </pre>
      {(open || clamped) && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={[
            "justify-self-start inline-flex items-center gap-[4px] rounded-[6px] border border-[color:var(--line)] px-[8px] py-[3px] text-[12px] leading-[1.08] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] transition-colors cursor-pointer",
            // Solid bg when sticky so scrolling text doesn't show through
            // the chip; subtle tinted bg otherwise to match other chips.
            open ? "sticky bottom-[8px] z-[1] bg-[color:var(--bg)]" : "bg-[color:var(--control-bg)]",
          ].join(" ")}
        >
          <svg viewBox="0 0 12 12" className="h-[8px] w-[8px]" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: open ? "rotate(180deg)" : "none" }}>
            <path d="M3 4.5l3 3 3-3" />
          </svg>
          <span>{open ? "Collapse" : "Expand"}</span>
        </button>
      )}
    </div>
  );
}

function BlockEyebrow({ label, accent }) {
  return (
    <span className={[BLOCK_EYEBROW_BASE, accent || "text-[color:var(--muted-soft)]"].join(" ")}>
      {label}
    </span>
  );
}

// Outer wrapper for every block. The visual separation between
// adjacent blocks comes from the divider line between rows in the
// turn body — the shell itself only provides the eyebrow + content
// stack and consistent min-width handling. Optional eyebrow is shown
// only when present, so plain text blocks render flush against the
// divider with no extra chrome.
function BlockShell({ eyebrow, accent, children, trailing = null }) {
  return (
    <div className="grid gap-[6px] min-w-0">
      {eyebrow ? (
        <span className="flex min-w-0 items-baseline gap-[8px]">
          <BlockEyebrow label={eyebrow} accent={accent} />
          {trailing ? <span className="ml-auto">{trailing}</span> : null}
        </span>
      ) : null}
      {children}
    </div>
  );
}

function TextBlockView({ text, role, turnIndex = null, blockIndex = null }) {
  // A Codex client wraps some user turns in a harness envelope (a voice
  // session's handoff, the answers to the agent's questions); the person's
  // words are inside it, so those are the body and the envelope is folded.
  // A block a signal fired into keeps the raw text: the mark's offsets were
  // measured on it.
  const highlightMap = useContext(SignalHighlightContext);
  const marked = Boolean(highlightMap && turnIndex != null && blockIndex != null && highlightMap.get(`${turnIndex}:${blockIndex}`)?.length);
  const wrapper = role === "user" && !marked ? parseCodexUserWrapper(text) : null;
  if (wrapper?.kind === "voice") return <VoiceHandoffBlock wrapper={wrapper} role={role} turnIndex={turnIndex} blockIndex={blockIndex} />;
  if (wrapper?.kind === "question_reply") return <QuestionReplyBlock wrapper={wrapper} />;
  // Plain text blocks (system reminders, user messages, assistant
  // text replies) render without an eyebrow. The role label on the
  // parent turn plus the divider between blocks is enough context.
  return (
    <BlockShell>
      <CollapsibleText text={text} role={role} turnIndex={turnIndex} blockIndex={blockIndex} />
    </BlockShell>
  );
}

// Codex Desktop's voice mode hands a spoken request to the text agent. The
// words are the body; the voice exchange that led to them is one fold away,
// and a handoff the client made on its own (the flush when the voice session
// ends) is named in the eyebrow.
function VoiceHandoffBlock({ wrapper, role, turnIndex, blockIndex }) {
  const source = wrapper.source ? wrapper.source.replace(/_/g, " ") : "";
  const lines = wrapper.transcript.length;
  return (
    <BlockShell eyebrow={`Voice handoff${source ? ` - ${source}` : ""}`} accent="text-[color:var(--eyebrow)]">
      <CollapsibleText text={wrapper.input || "(no spoken input)"} defaultOpen role={role} turnIndex={turnIndex} blockIndex={blockIndex} />
      {lines ? (
        <details className="min-w-0 rounded-[7px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[8px] py-[4px]" data-voice-transcript={lines}>
          <summary className="cursor-pointer list-none text-[12px] text-[color:var(--muted)]">
            Voice transcript - {lines} line{lines === 1 ? "" : "s"}
          </summary>
          <div className="grid gap-[3px] pt-[6px] font-mono text-[11px] leading-[1.5]">
            {wrapper.transcript.map((line, i) => (
              <div key={i} className="grid grid-cols-[64px_minmax(0,1fr)] gap-[6px] min-w-0">
                <span className="pt-[2px] text-[9.5px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">{line.speaker}</span>
                <span className="whitespace-pre-wrap [overflow-wrap:anywhere] text-[color:var(--ink)]">{line.text}</span>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </BlockShell>
  );
}

// The answers a person gave to the agent's request_user_input questions,
// which the client sends back as one user turn. Each question is shown with
// its answer so the reader sees the exchange, not the JSON that carried it.
function QuestionReplyBlock({ wrapper }) {
  const count = wrapper.items.length;
  return (
    <BlockShell eyebrow={`Answers to the agent's questions - ${count}`} accent="text-[color:var(--eyebrow)]">
      <div className="grid gap-[6px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg-strong,var(--control-bg))] px-[8px] py-[6px] text-[11px] leading-[1.5]" data-question-replies={count}>
        {wrapper.items.map((item, i) => (
          <div key={i} className="grid gap-[2px] min-w-0">
            <div className="whitespace-pre-wrap [overflow-wrap:anywhere] text-[color:var(--muted)]">{item.question}</div>
            <div className="whitespace-pre-wrap [overflow-wrap:anywhere] text-[color:var(--ink)]">{item.answer || "(no answer)"}</div>
          </div>
        ))}
      </div>
    </BlockShell>
  );
}

// A code-mode cell's input is one JS program under an `input` key. The JSON
// tree renders it as a single escaped line — unreadable — so the program text
// itself is the body. Tight gate: only the cell-shaped names, only a string.
function cellSourceOf(block) {
  if (block?.name !== "exec" && block?.name !== "js") return null;
  const src = block.input?.input;
  return typeof src === "string" && src ? src : null;
}

function ToolUseBlockView({ block, role = null, turnIndex = null, blockIndex = null }) {
  // Chips for this block are aggregated at the turn level (rendered as
  // a scannable rail outside the accordion). Inside the expanded block
  // we just show the call shape — the JSON is the source of truth that
  // chips were extracted from.
  //
  // When a signal fired INTO this call, the JSON tree can't carry the mark (it's
  // not searchable text), so we switch to a searchable raw-text rendering for
  // that block only — unmarked calls keep the tree.
  const highlightMap = useContext(SignalHighlightContext);
  const marks = highlightMap && turnIndex != null && blockIndex != null
    ? highlightMap.get(`${turnIndex}:${blockIndex}`)
    : null;
  const cellSource = cellSourceOf(block);
  // Endpoint trace activity records when the call actually ran. It is read for
  // the focal turn only, so most history blocks match nothing and show no
  // stamp at all rather than claiming the run was never recorded.
  const stamp = toolStampOf(useContext(ToolStampContext), block);
  return (
    <BlockShell
      eyebrow={`Call · ${block.name || "tool"}${cellSource ? " · code cell" : ""}`}
      accent="text-[color:var(--eyebrow)]"
      trailing={<TraceStamp stamp={stamp} />}
    >
      {block.input != null && (
        marks && marks.length
          ? <CollapsibleText text={toolUseBodyText(block)} defaultOpen role={role} turnIndex={turnIndex} blockIndex={blockIndex} />
          : cellSource
            ? <CollapsibleText text={cellSource} role={role} turnIndex={turnIndex} blockIndex={blockIndex} />
            : <pre className="m-0 max-w-full overflow-hidden whitespace-pre-wrap [overflow-wrap:anywhere] [word-break:break-word] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg-strong,var(--control-bg))] px-[8px] py-[6px] font-mono text-[11px] leading-[1.52]">
              {block.typedInput ? formatConversationValue(block.typedInput) : <JsonValue value={block.input} indent={0} defaultOpen />}
            </pre>
      )}
    </BlockShell>
  );
}

export const CaptureSourceContext = createContext("");

export const COMPLIANCE_RESULT_OMITTED = "Results not included by the Compliance API.";

function ToolResultBlockView({ block, role, turnIndex = null, blockIndex = null }) {
  const captureSource = useContext(CaptureSourceContext);
  const linked = block.linkedToolName ? `Result · ${block.linkedToolName}` : "Result";
  if (captureSource === "compliance_sync" && !block.isError && Array.isArray(block.content) && block.content.length === 0) {
    return (
      <BlockShell eyebrow={linked} accent="text-[color:var(--muted)]">
        <span data-compliance-result-omitted="" className="text-[11px] leading-[1.4] text-[color:var(--muted-soft)]">
          {COMPLIANCE_RESULT_OMITTED}
        </span>
      </BlockShell>
    );
  }
  // A code-mode cell's output arrives inside a host envelope ("Script
  // completed / Wall time / Output:"). Lift the envelope's facts into the
  // eyebrow — status, exit code, wall time, whether the host truncated — and
  // show the program's actual output as the body.
  const { env, body } = toolResultReading(block);
  const failed = block.isError || (env?.exitCode != null && env.exitCode !== 0);
  const accent = failed ? "text-[color:var(--red,#c45)]" : "text-[color:var(--muted)]";
  const eyebrow = [
    linked,
    cellEnvelopeNote(env),
    !env && block.isError ? "error" : null,
  ].filter(Boolean).join(" · ");
  return (
    <BlockShell eyebrow={eyebrow} accent={accent}>
      <CollapsibleText text={body} role={role} turnIndex={turnIndex} blockIndex={blockIndex} />
    </BlockShell>
  );
}

// An offered tool's definition renders as searchable text (toolDefText), so a
// fired mark lands. Name rides the eyebrow, not the body.
function ToolDefBlockView({ block, role = null, turnIndex = null, blockIndex = null }) {
  return (
    <BlockShell eyebrow={`Tool · ${block.name || "tool"}`} accent="text-[color:var(--eyebrow)]">
      <CollapsibleText text={toolDefText(block)} role={role} turnIndex={turnIndex} blockIndex={blockIndex} />
    </BlockShell>
  );
}

// Styled to match `ThinkingNode` in ChatConsole; keep them in step. Local
// `Chevron` because three other components already define their own.
function ThinkingChevron({ expanded }) {
  return (
    <svg
      viewBox="0 0 10 10"
      className={[
        "h-[9px] w-[9px] shrink-0 text-[color:var(--muted-soft)] transition-transform duration-150",
        expanded ? "rotate-90" : "",
      ].join(" ")}
      aria-hidden="true"
    >
      <path d="M3.5 2L6.5 5L3.5 8" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ThinkingBlockView({ text, turnIndex = null, blockIndex = null }) {
  const highlightMap = useContext(SignalHighlightContext);
  const marks = highlightMap && turnIndex != null && blockIndex != null
    ? highlightMap.get(`${turnIndex}:${blockIndex}`)
    : null;
  const hasMarks = !!(marks && marks.length);
  const [expanded, setExpanded] = useState(hasMarks);
  useEffect(() => {
    if (hasMarks) setExpanded(true);
  }, [hasMarks]);
  return (
    <BlockShell>
      <div>
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className="flex w-full items-center gap-[6px] bg-transparent text-left text-[12px] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] cursor-pointer"
        >
          <ThinkingChevron expanded={expanded} />
          <span>Thinking</span>
        </button>
        {expanded ? (
          <div className="ml-[3px] mt-[4px] whitespace-pre-wrap border-l border-[color:var(--line)] pl-[10px] text-[11px] leading-[1.5] text-[color:var(--muted)] [overflow-wrap:anywhere]">
            {hasMarks ? <HighlightedText text={text || ""} matches={marks} /> : text}
          </div>
        ) : null}
      </div>
    </BlockShell>
  );
}

function ImageBlockView({ source }) {
  const mediaType = source?.media_type || "image";
  return (
    <BlockShell eyebrow={`Image · ${mediaType}`}>
      <span className="text-[11px] text-[color:var(--muted)]">
        Image preview unavailable
      </span>
    </BlockShell>
  );
}

function UnknownBlockView({ raw }) {
  return (
    <BlockShell eyebrow="Unknown block">
      <pre className="m-0 max-w-full whitespace-pre-wrap [overflow-wrap:anywhere] [word-break:break-word] font-mono text-[11px] leading-[1.52] text-[color:var(--muted)]">
        {JSON.stringify(raw, null, 2)}
      </pre>
    </BlockShell>
  );
}

function TurnAnswerGapView({ gap, now }) {
  const overdue = isOverdue(gap, now);
  return (
    <div
      data-turn-answer-gap={overdue ? "overdue" : "pending"}
      className="flex flex-wrap items-center gap-x-[8px] gap-y-[4px] py-[10px] text-[11px] leading-[1.4] text-[color:var(--muted)]"
      role="status"
    >
      <span className="inline-flex items-center gap-[5px] text-[12px] text-[color:var(--muted-soft)]">
        <span className={`h-[6px] w-[6px] rounded-full ${overdue ? "border border-[color:var(--muted)]" : "animate-pulse bg-[color:var(--muted)]"}`} />
        Answer
      </span>
      <span>{turnAnswerGapText(gap, now)}</span>
    </div>
  );
}

// Origin-synthesized content blocks from the M365 Copilot capture surface
// (provider=microsoft): an attachment reference, an open-document context
// handle, or a link. They aren't standard block types, so the parity-pinned
// normalizer passes them through as `unknown` with the original object on
// `raw`. Render them as labelled references rather than raw JSON — otherwise
// every Copilot turn that attaches a file or cites a doc shows "Unknown block".
// Captured URLs/paths are untrusted, so they render as inert text, never links.
function OriginRefBlockView({ eyebrow, primary, detail }) {
  return (
    <BlockShell eyebrow={eyebrow}>
      <span className="text-[11px] text-[color:var(--muted)]">{primary}</span>
      {detail ? (
        <span className="text-[11px] text-[color:var(--muted-soft)] font-mono [overflow-wrap:anywhere] [word-break:break-word]">
          {detail}
        </span>
      ) : null}
    </BlockShell>
  );
}

function AttachmentBlockView({ raw }) {
  const primary = (typeof raw?.name === "string" && raw.name) || "(unnamed attachment)";
  const bits = [];
  if (typeof raw?.content_type === "string" && raw.content_type.includes("/")) bits.push(raw.content_type);
  if (typeof raw?.url === "string" && raw.url) bits.push(raw.url);
  return <OriginRefBlockView eyebrow="Attachment" primary={primary} detail={bits.join(" · ") || null} />;
}

function ContextBlockView({ raw }) {
  const primary = (typeof raw?.display_name === "string" && raw.display_name) || "(document context)";
  const detail = (typeof raw?.reference === "string" && raw.reference) || null;
  return <OriginRefBlockView eyebrow="Context" primary={primary} detail={detail} />;
}

function LinkBlockView({ raw }) {
  const url = (typeof raw?.url === "string" && raw.url) || "(no url)";
  return (
    <BlockShell eyebrow="Link">
      <span className="text-[11px] text-[color:var(--muted)] font-mono [overflow-wrap:anywhere] [word-break:break-word]">
        {url}
      </span>
    </BlockShell>
  );
}

const str = (v) => (typeof v === "string" && v ? v : null);

const originRaw = (block) => block?.raw ?? block;
const refKey = (name) => (typeof name === "string" && name.trim() ? name.trim().toLowerCase() : null);

export function collapseDocumentRefs(blocks) {
  const attachmentByName = new Map();
  for (const block of blocks) {
    const raw = originRaw(block);
    const key = raw?.type === "origin_attachment" ? refKey(raw.name) : null;
    if (key && !attachmentByName.has(key)) attachmentByName.set(key, raw);
  }
  if (attachmentByName.size === 0) return blocks;
  const referenceByName = new Map();
  const kept = blocks.filter((block) => {
    const raw = originRaw(block);
    const key = raw?.type === "origin_context" ? refKey(raw.display_name) : null;
    if (!key || !attachmentByName.has(key)) return true;
    if (str(raw.reference) && !referenceByName.has(key)) referenceByName.set(key, raw.reference);
    return false;
  });
  if (kept.length === blocks.length) return blocks;
  return kept.map((block) => {
    const raw = originRaw(block);
    const key = raw?.type === "origin_attachment" ? refKey(raw.name) : null;
    if (!key || str(raw.url) || !referenceByName.has(key) || attachmentByName.get(key) !== raw) return block;
    const merged = { ...raw, url: referenceByName.get(key) };
    return block?.raw ? { ...block, raw: merged } : merged;
  });
}

// `origin_file` / `origin_generated_file` share a payload across anthropic-sync
// and openai-sync (`id` / `filename` / `mime_type`), which is why the type names
// are shared too — a renderer reading one connector's keys would blank on the
// other's.
function FileBlockView({ raw, eyebrow }) {
  const primary = str(raw?.filename) || str(raw?.id) || "(unnamed file)";
  const bits = [str(raw?.mime_type)];
  if (str(raw?.filename)) bits.push(str(raw?.id));
  return <OriginRefBlockView eyebrow={eyebrow} primary={primary} detail={bits.filter(Boolean).join(" · ") || null} />;
}

function ArtifactBlockView({ raw }) {
  const primary = str(raw?.title) || str(raw?.id) || "(untitled artifact)";
  const bits = [str(raw?.artifact_type), str(raw?.version_id) && `v${raw.version_id}`];
  return <OriginRefBlockView eyebrow="Artifact" primary={primary} detail={bits.filter(Boolean).join(" · ") || null} />;
}

// Citations on an answer. The payload is whatever the provider returned, so
// nothing here reads a field by name beyond a count — a title/url schema we
// guessed would render blank the moment it is wrong, where a count is right for
// any shape. Bytes stay out; this records that the answer cited sources.
function AnnotationsBlockView({ raw }) {
  const count = Array.isArray(raw?.annotations) ? raw.annotations.length : null;
  return (
    <OriginRefBlockView
      eyebrow="Citations"
      primary={count === null ? "Answer carried citations" : `${count} cited source${count === 1 ? "" : "s"}`}
      detail={null}
    />
  );
}

// An app / MCP call an assistant turn triggered. Deliberately header-only: the
// connector emits a `tool_use` / `tool_result` pair alongside this block
// carrying the same `input` / `output`, so rendering the payload here would
// print every app call's arguments and results twice.
function AppCallBlockView({ raw }) {
  const primary = str(raw?.app_name) || str(raw?.app_id) || "(unnamed app)";
  const bits = [str(raw?.app_type), str(raw?.log_type)];
  return <OriginRefBlockView eyebrow="App call" primary={primary} detail={bits.filter(Boolean).join(" · ") || null} />;
}

// Every `origin_*` content block the three server-side connectors emit, and the
// contract this switch has to satisfy. There is no compiler backstop across the
// language boundary: an emitted type missing from `BlockView` below renders as
// "Unknown block" over raw JSON under a customer's prompt, which is how
// anthropic-sync's files and artifacts read until this landed.
//
// Producers, by connector:
//   anthropic-sync   origin_file, origin_generated_file, origin_artifact
//   copilot-sync     origin_attachment, origin_link, origin_context
//   openai-sync      origin_annotations, origin_file, origin_context,
//                    origin_app_call
//
// `origin_unmodelled` is deliberately absent: it rides a text block as a rider
// key rather than taking a type of its own, and the dashboard drops it by
// design (give it a type and it becomes a debug panel under a prompt).
//
// Adding a producer-side type means adding it here, adding its arm below, and
// extending `ContextPanel.originBlocks.test.jsx` — in the same change.
export const ORIGIN_BLOCK_TYPES = Object.freeze([
  "origin_annotations",
  "origin_app_call",
  "origin_artifact",
  "origin_attachment",
  "origin_context",
  "origin_file",
  "origin_generated_file",
  "origin_link",
]);

function BlockView({ block, role, turnIndex = null, blockIndex = null }) {
  switch (block?.type) {
    case "text":        return <TextBlockView text={block.text} role={role} turnIndex={turnIndex} blockIndex={blockIndex} />;
    case "tool_use":    return <ToolUseBlockView block={block} role={role} turnIndex={turnIndex} blockIndex={blockIndex} />;
    case "tool_result": return <ToolResultBlockView block={block} role={role} turnIndex={turnIndex} blockIndex={blockIndex} />;
    case "tool_def":    return <ToolDefBlockView block={block} role={role} turnIndex={turnIndex} blockIndex={blockIndex} />;
    case "thinking":    return <ThinkingBlockView text={block.thinking} turnIndex={turnIndex} blockIndex={blockIndex} />;
    case "image":       return <ImageBlockView source={block.source} />;
    default: {
      // origin_* blocks arrive wrapped as {type:"unknown", raw:{type:"origin_…"}};
      // dispatch on the underlying type so they render as references, not JSON.
      const raw = block?.raw ?? block;
      switch (raw?.type) {
        case "origin_attachment":     return <AttachmentBlockView raw={raw} />;
        case "origin_context":        return <ContextBlockView raw={raw} />;
        case "origin_link":           return <LinkBlockView raw={raw} />;
        case "origin_file":           return <FileBlockView raw={raw} eyebrow="File" />;
        case "origin_generated_file": return <FileBlockView raw={raw} eyebrow="Generated file" />;
        case "origin_artifact":       return <ArtifactBlockView raw={raw} />;
        case "origin_annotations":    return <AnnotationsBlockView raw={raw} />;
        case "origin_app_call":       return <AppCallBlockView raw={raw} />;
        default:                      return <UnknownBlockView raw={raw} />;
      }
    }
  }
}

// Aggregate chips from every tool_use block in a turn. Facets are
// arrays of plain strings (see setsToArrays in parseApiRequest.js) —
// the value is both the scope id and the label. Dedupes on
// `${dim}:${id}` so a turn that calls Bash three times still renders
// "TOOL Bash" once. Order: tools → commands → files → hosts → urls →
// skills, matching the per-block render order.
function aggregateTurnChips(turn) {
  const seen = new Set();
  const out = [];
  const push = (link) => {
    const key = `${link.dim}:${link.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(link);
  };
  for (const block of turn.blocks || []) {
    if (block?.type !== "tool_use" || !block.facets) continue;
    const f = block.facets;
    for (const tc   of f.toolCalls || []) push({ dim: "tools",    id: tc,   label: tc });
    for (const cmd  of f.commands  || []) push({ dim: "commands", id: cmd,  label: cmd });
    for (const fp   of f.files     || []) push({ dim: "files",    id: fp,   label: shortenPath(fp), title: fp });
    for (const host of f.hosts     || []) push({ dim: "hosts",    id: host, label: host });
    for (const url  of f.urls      || []) push({ dim: "urls",     id: url,  label: url, title: url });
    for (const sk   of f.skills    || []) push({ dim: "skills",   id: sk,   label: sk });
  }
  return out;
}

// Exported so other surfaces can render a message/step sequence with
// the exact trace-detail chrome — e.g. the workflow run log, whose
// agent execution is structurally the same as a trace's turns.
// `stamp` is the three-state value buildTurnStamps produces: a stamp, `null`
// for a captured item with no recorded time, or omitted entirely for a caller
// that is not in the dating business. Both missing states render no label.
export function TurnBlock({ turn, defaultOpen = false, onScopeNavigate, onTraceInfluence = null, turnIndex = null, focused = false, onFocusTurn = null, stamp = undefined, answerGap = null, kinds = null }) {
  const roleColor = "text-[color:var(--eyebrow)]";
  const allBlocks = collapseDocumentRefs(turn.blocks || []);
  // A message-type filter shows only the blocks of the chosen types; each
  // keeps its index in the turn so signal and search marks still land.
  const shown = blocksOfKinds(turn, allBlocks, kinds);
  const blocks = shown.map(([b]) => b);
  const chips = aggregateTurnChips(turn);
  const showChips = chips.length > 0 && onScopeNavigate;
  // Does any block in this turn carry a signal highlight? Drives both the
  // collapsed-header marker (so a long history is scannable for hits) and
  // auto-opening the accordion — a buried highlight should be visible on open.
  const highlightMap = useContext(SignalHighlightContext);
  const { signalAccent, searchHits } = useMemo(() => {
    if (!highlightMap || turnIndex == null) return { signalAccent: null, searchHits: 0 };
    const prefix = `${turnIndex}:`;
    let best = null; // risk > pii > audit > other
    let hits = 0;
    const rank = (t) => (t === "risk" ? 3 : t === "pii" ? 2 : t === "audit" ? 1 : 0);
    for (const [k, arr] of highlightMap) {
      if (!k.startsWith(prefix)) continue;
      for (const m of arr) {
        if (m.type === SEARCH_MARK_TYPE) hits += 1;
        const ns = typeof m.type === "string" ? m.type.split(".")[0] : "";
        if (!best || rank(ns) > rank(best)) best = ns;
      }
    }
    return { signalAccent: best == null ? null : markAccent(`${best}.x`), searchHits: hits };
  }, [highlightMap, turnIndex]);
  const [open, setOpen] = useState(defaultOpen || signalAccent != null);
  useEffect(() => {
    if (signalAccent != null) setOpen(true);
  }, [signalAccent]);

  // data-turn-index is the SignalLocator's jump/flash anchor and must be
  // unique per turn (one anchor per history row).

  // Collapsed preview: pull the first text block's leading characters
  // so the accordion header still hints at the turn's content. A Codex
  // envelope previews as the person's words, the way its open block reads.
  const firstTextRaw = blocks.find((b) => b?.type === "text")?.text;
  const envelope = turn.role === "user" ? parseCodexUserWrapper(firstTextRaw) : null;
  const firstText = (envelope?.kind === "voice" ? envelope.input : envelope?.kind === "question_reply" ? envelope.items.map((item) => item.answer).filter(Boolean).join(" / ") : "")
                 || firstTextRaw
                 || (blocks[0]?.type === "tool_use" ? `${blocks[0].name || "tool"} call` : "")
                 || (blocks[0]?.type === "tool_result" ? `${blocks[0].linkedToolName || "tool"} result` : "");

  return (
    <div className="min-w-0 group" data-turn-index={turnIndex == null ? undefined : turnIndex}>
      {/* Bottom padding only while expanded: the turn number is parked in the
          card's bottom-right corner, and an open card's last block runs all
          the way down to the border without it. */}
      {/* The focused turn rings itself: the border warms toward the eyebrow
          and a 1px halo sits just outside it. Border colour plus box-shadow,
          never a width change — the card must not shift when focus moves. The
          mix keeps it quieter than the signal accent that can live inside the
          same card. */}
      <div
        data-turn-focused={focused && turnIndex != null ? "" : undefined}
        style={focused && turnIndex != null ? {
          borderColor: "color-mix(in srgb, var(--eyebrow) 55%, var(--line))",
          boxShadow: "0 0 0 1px color-mix(in srgb, var(--eyebrow) 22%, transparent)",
        } : undefined}
        className={[
          "relative grid rounded-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] min-w-0",
          open && turnIndex != null ? "pb-[9px]" : "",
        ].join(" ")}
      >
        <div className="flex items-stretch min-w-0">
          <button
            type="button"
            onClick={() => {
              // Opening a card is the deliberate act that focuses its turn:
              // the reader has said "this one" and every other reading should
              // land there. Closing that same card is the inverse and clears
              // it. Closing a card that is NOT the focused one says nothing.
              if (turnIndex != null) {
                if (!open) onFocusTurn?.(turnIndex);
                else if (focused) onFocusTurn?.(null);
              }
              setOpen((v) => !v);
            }}
            className="flex-1 grid gap-[3px] px-[8px] py-[6px] text-left cursor-pointer min-w-0"
          >
            <div className="flex items-center gap-[6px]">
              <span
                className="grid h-[12px] w-[12px] shrink-0 place-items-center text-[color:var(--muted)] transition-transform [&_svg]:block [&_svg]:h-[8px] [&_svg]:w-[8px]"
                style={{ transform: open ? "rotate(90deg)" : "none" }}
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 4l4 4-4 4" />
                </svg>
              </span>
              <span className={["text-[12px] leading-[1.08] font-medium", roleColor].join(" ")}>
                {turn.label}
              </span>
              <span className="text-[11px] leading-[1.08] text-[color:var(--muted-soft)]">
                {blocks.length < allBlocks.length ? `${blocks.length} of ` : ""}{allBlocks.length} {allBlocks.length === 1 ? "block" : "blocks"}
              </span>
              {turn.fromThisCapture ? (
                // The conversation's turns come from many captures; this is one
                // the record dates to the request this tab is for.
                <span className="text-[11px] leading-[1.08] text-[color:var(--eyebrow)]" data-from-this-capture="true">
                  this capture
                </span>
              ) : null}
              {signalAccent && (
                // A turn carrying a signal hit gets a dot in the hit's accent —
                // so a long collapsed history is scannable for where to look.
                <span
                  className="inline-flex items-center gap-[4px] text-[11px] leading-[1.08]"
                  style={{ color: signalAccent }}
                >
                  <span className="h-[5px] w-[5px] rounded-full" style={{ backgroundColor: signalAccent }} />
                  {searchHits ? `${searchHits} ${searchHits === 1 ? "match" : "matches"}` : "signal"}
                </span>
              )}
              {answerGap ? (
                <span data-turn-answer-tag="" className="text-[11px] leading-[1.08] text-[color:var(--muted-soft)]">
                  {isOverdue(answerGap) ? "no answer" : "awaiting answer"}
                </span>
              ) : null}
            </div>
            {!open && firstText && (
              <span className="line-clamp-2 pl-[18px] text-[11px] leading-[1.32] text-[color:var(--muted-soft)]">
                {firstText.slice(0, 160)}
              </span>
            )}
          </button>
          {/* Beside the toggle, not in it: the stamp opens the capture its time
              came from, and a button cannot hold another. */}
          {stamp != null ? (
            <span className="flex shrink-0 self-start py-[7px] pr-[8px]">
              <TraceStamp stamp={stamp} />
            </span>
          ) : null}
          {onTraceInfluence && (
            <HoverTooltip label="Open an influence trace for this turn" placement="left" wide>
              <button
                type="button"
                aria-label="Trace what influenced this turn"
                onClick={(e) => { e.stopPropagation(); onTraceInfluence(turnIndex, turn); }}
                className="shrink-0 self-start grid place-items-center px-[8px] py-[7px] text-[color:var(--muted-soft)] opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-[color:var(--green)] transition-[opacity,color] cursor-pointer [&_svg]:block [&_svg]:h-[13px] [&_svg]:w-[13px]"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
                </svg>
              </button>
            </HoverTooltip>
          )}
        </div>
        {/* The turn's number, parked in the card's bottom-right corner: a
            label you look up when you need the coordinate, never part of the
            card's headline. It is the same number the Lanes axis and dock
            print, which is what lets a reader carry a turn between readings by
            eye. Absent for consumers that render a step sequence with no trace
            turn index (the workflow run log), hence the gate. */}
        {turnIndex == null ? null : (
          <span
            data-turn-number=""
            title={`Turn ${turnIndex}`}
            aria-hidden="true"
            className={`pointer-events-none absolute bottom-[2px] right-[5px] font-mono text-[9px] leading-[1.08] tabular-nums ${
              focused ? "text-[color:var(--eyebrow)] opacity-90" : "text-[color:var(--muted-soft)] opacity-45"
            }`}
          >
            {turnIndex}
          </span>
        )}
        {open && (
          <div className="grid border-t border-[color:var(--line)] px-[8px] min-w-0 divide-y divide-[color:var(--line)]">
            {shown.map(([b, i]) => (
              <div key={i} className="min-w-0 py-[10px]">
                <BlockView block={b} role={turn.role} turnIndex={turnIndex} blockIndex={i} />
              </div>
            ))}
            {answerGap ? <TurnAnswerGapView gap={answerGap} /> : null}
          </div>
        )}
      </div>
      {showChips && (
        <div className="relative ml-[14px] grid gap-[1px] pt-[3px] pb-[2px] min-w-0">
          <span className="absolute left-0 top-0 w-[1px] bg-[color:var(--line)]" style={{ bottom: "calc(2px + 13px)" }} />
          {chips.map((link) => (
            <div key={`${link.dim}:${link.id}`} className="flex items-center min-w-0 min-h-[26px] pl-[14px] relative">
              <span className="absolute left-0 top-1/2 w-[14px] h-[1px] bg-[color:var(--line)]" />
              <ScopeChip
                dim={link.dim}
                label={link.label}
                title={link.title}
                onClick={() => onScopeNavigate(link.dim, link.id)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Collapsible syntax-highlighted JSON tree ────────────────────────

const JSON_KEY_CLASS = "text-[color:var(--eyebrow)]";
const JSON_STRING_CLASS = "text-[color:var(--green)]";
const JSON_NUMBER_CLASS = "text-[color:var(--ink)]";
const JSON_BOOL_CLASS = "text-[color:var(--muted)]";
const JSON_NULL_CLASS = "text-[color:var(--muted)]";
const JSON_BRACE_CLASS = "text-[color:var(--muted-soft)]";
const JSON_BASE = "font-mono text-[11px] leading-[1.52]";

function JsonValue({ value, indent = 0, defaultOpen = true }) {
  if (value === null) return <span className={JSON_NULL_CLASS}>null</span>;
  if (typeof value === "boolean") return <span className={JSON_BOOL_CLASS}>{String(value)}</span>;
  if (typeof value === "number") return <span className={JSON_NUMBER_CLASS}>{String(value)}</span>;
  if (typeof value === "string") {
    // Truncate very long strings in the inline view
    const display = JSON.stringify(value);
    return <span className={JSON_STRING_CLASS}>{display}</span>;
  }
  if (Array.isArray(value)) return <JsonArray value={value} indent={indent} defaultOpen={defaultOpen} />;
  if (typeof value === "object") return <JsonObject value={value} indent={indent} defaultOpen={defaultOpen} />;
  return <span>{String(value)}</span>;
}

function JsonObject({ value, indent, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const entries = Object.entries(value);
  if (entries.length === 0) return <span className={JSON_BRACE_CLASS}>{"{}"}</span>;

  const pad = " ".repeat(indent + 2);
  const closePad = " ".repeat(indent);

  if (!open) {
    return (
      <span>
        <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
          <span className={JSON_BRACE_CLASS}>{"{ "}</span>
          <span className={JSON_BOOL_CLASS}>…{entries.length} keys</span>
          <span className={JSON_BRACE_CLASS}>{" }"}</span>
        </button>
      </span>
    );
  }

  return (
    <span>
      <button type="button" onClick={() => setOpen(false)} className="cursor-pointer">
        <span className={JSON_BRACE_CLASS}>{"{"}</span>
      </button>
      {"\n"}
      {entries.map(([k, v], i) => (
        <span key={k}>
          {pad}<span className={JSON_KEY_CLASS}>{JSON.stringify(k)}</span>
          <span className={JSON_BRACE_CLASS}>: </span>
          <JsonValue value={v} indent={indent + 2} defaultOpen={defaultOpen} />
          {i < entries.length - 1 ? <span className={JSON_BRACE_CLASS}>,</span> : null}
          {"\n"}
        </span>
      ))}
      {closePad}<span className={JSON_BRACE_CLASS}>{"}"}</span>
    </span>
  );
}

function JsonArray({ value, indent, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  if (value.length === 0) return <span className={JSON_BRACE_CLASS}>{"[]"}</span>;

  const pad = " ".repeat(indent + 2);
  const closePad = " ".repeat(indent);

  if (!open) {
    return (
      <span>
        <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
          <span className={JSON_BRACE_CLASS}>{"[ "}</span>
          <span className={JSON_BOOL_CLASS}>…{value.length} items</span>
          <span className={JSON_BRACE_CLASS}>{" ]"}</span>
        </button>
      </span>
    );
  }

  return (
    <span>
      <button type="button" onClick={() => setOpen(false)} className="cursor-pointer">
        <span className={JSON_BRACE_CLASS}>{"["}</span>
      </button>
      {"\n"}
      {value.map((v, i) => (
        <span key={i}>
          {pad}<JsonValue value={v} indent={indent + 2} defaultOpen={defaultOpen} />
          {i < value.length - 1 ? <span className={JSON_BRACE_CLASS}>,</span> : null}
          {"\n"}
        </span>
      ))}
      {closePad}<span className={JSON_BRACE_CLASS}>{"]"}</span>
    </span>
  );
}

// ── Virtualized JSON view (for large raw payloads) ──────────────────
//
// The recursive JsonValue renderer above is fine for the small tool-call
// inputs it draws inline in the turns view, but an LLM `api_request` blob
// (full message history + system prompt + every tool's JSON schema) is
// routinely thousands of lines — fully expanding it into the DOM froze the tab
// on switch to the raw view. This view flattens the parsed JSON to a flat line
// list and renders only the lines in the scroll viewport (± overscan),
// measuring each rendered row so wrapped lines keep their true height.
//
// It owns its windowing end-to-end (rather than reusing the shared
// VirtualFolderList) to be robust to the trace panel's scroll context, which
// broke the shared path three ways:
//   1. It finds the scroll parent generically — the nearest OverlayScrollbars
//      viewport, or any ancestor with a scrollable overflow — not via a single
//      attribute.
//   2. If none is found it falls back to its OWN scroll container, so it never
//      degrades to mounting every row.
//   3. It force-recomputes the window on every scroll / resize / measure and
//      treats viewportH===0 as "not measured yet" (keep the bootstrap window)
//      rather than "show everything" — so a deferred-sized viewport (OS sizes
//      its viewport on a rAF, after mount) can never lock it onto the full
//      range the way reconcileWindow's hysteresis did.
const EST_ROW_H = 20; // height estimate for not-yet-measured rows
const OVERSCAN_PX = 600; // rendered buffer above/below the viewport
const BOOTSTRAP_ROWS = 60; // rows rendered on first paint, before measurement
const RAW_STR_CLIP = 4000; // clip a lone oversized string in the raw JSON view (full value stays in Download)

// Flatten a parsed JSON value into a flat array of line descriptors, honouring
// the `collapsed` set (node ids whose children are hidden). Each node id is a
// positional path ("r", "r/0", "r/0/3", …) so it stays stable across collapses.
function flattenJson(root, collapsed) {
  const out = [];

  function walk(value, indent, keyLabel, isLast, nid) {
    const comma = !isLast;

    if (value === null) {
      out.push({ id: nid, kind: "leaf", indent, keyLabel, val: { t: "null", c: JSON_NULL_CLASS }, clip: 0, comma });
      return;
    }
    const ty = typeof value;
    if (ty === "boolean") {
      out.push({ id: nid, kind: "leaf", indent, keyLabel, val: { t: String(value), c: JSON_BOOL_CLASS }, clip: 0, comma });
      return;
    }
    if (ty === "number") {
      out.push({ id: nid, kind: "leaf", indent, keyLabel, val: { t: String(value), c: JSON_NUMBER_CLASS }, clip: 0, comma });
      return;
    }
    if (ty === "string") {
      const s = JSON.stringify(value);
      const clip = s.length > RAW_STR_CLIP ? s.length - RAW_STR_CLIP : 0;
      out.push({ id: nid, kind: "leaf", indent, keyLabel, val: { t: clip ? s.slice(0, RAW_STR_CLIP) : s, c: JSON_STRING_CLASS }, clip, comma });
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        out.push({ id: nid, kind: "empty", indent, keyLabel, text: "[]", comma });
        return;
      }
      if (collapsed.has(nid)) {
        out.push({ id: nid, kind: "collapsed", indent, keyLabel, open: "[", close: "]", label: `…${value.length} items`, nid, comma });
        return;
      }
      out.push({ id: `${nid}:o`, kind: "open", indent, keyLabel, brace: "[", nid });
      const n = value.length;
      for (let i = 0; i < n; i++) walk(value[i], indent + 2, null, i === n - 1, `${nid}/${i}`);
      out.push({ id: `${nid}:c`, kind: "close", indent, brace: "]", comma });
      return;
    }
    // plain object
    const entries = Object.entries(value);
    if (entries.length === 0) {
      out.push({ id: nid, kind: "empty", indent, keyLabel, text: "{}", comma });
      return;
    }
    if (collapsed.has(nid)) {
      out.push({ id: nid, kind: "collapsed", indent, keyLabel, open: "{", close: "}", label: `…${entries.length} keys`, nid, comma });
      return;
    }
    out.push({ id: `${nid}:o`, kind: "open", indent, keyLabel, brace: "{", nid });
    const n = entries.length;
    for (let i = 0; i < n; i++) {
      walk(entries[i][1], indent + 2, JSON.stringify(entries[i][0]), i === n - 1, `${nid}/${i}`);
    }
    out.push({ id: `${nid}:c`, kind: "close", indent, brace: "}", comma });
  }

  walk(root, 0, null, true, "r");
  return out;
}

// One rendered JSON line — a fragment of styled spans. The wrapping row <div>
// (with the measure ref + data-line-id) is supplied by the list so it can
// observe the row's height.
const JsonLine = memo(function JsonLine({ line, onToggle }) {
  const pad = line.indent > 0 ? " ".repeat(line.indent) : "";
  const keyPrefix =
    line.keyLabel != null ? (
      <>
        <span className={JSON_KEY_CLASS}>{line.keyLabel}</span>
        <span className={JSON_BRACE_CLASS}>: </span>
      </>
    ) : null;
  const trailComma = line.comma ? <span className={JSON_BRACE_CLASS}>,</span> : null;

  if (line.kind === "leaf") {
    return (
      <>
        {pad}{keyPrefix}
        <span className={line.val.c}>{line.val.t}</span>
        {line.clip ? <span className={JSON_NULL_CLASS}>{`…+${line.clip} chars`}</span> : null}
        {trailComma}
      </>
    );
  }
  if (line.kind === "empty") {
    return (<>{pad}{keyPrefix}<span className={JSON_BRACE_CLASS}>{line.text}</span>{trailComma}</>);
  }
  if (line.kind === "open") {
    return (
      <>
        {pad}{keyPrefix}
        <button type="button" onClick={() => onToggle(line.nid)} className="cursor-pointer">
          <span className={JSON_BRACE_CLASS}>{line.brace}</span>
        </button>
      </>
    );
  }
  if (line.kind === "close") {
    return (<>{pad}<span className={JSON_BRACE_CLASS}>{line.brace}</span>{trailComma}</>);
  }
  // collapsed
  return (
    <>
      {pad}{keyPrefix}
      <button type="button" onClick={() => onToggle(line.nid)} className="cursor-pointer">
        <span className={JSON_BRACE_CLASS}>{`${line.open} `}</span>
        <span className={JSON_BOOL_CLASS}>{line.label}</span>
        <span className={JSON_BRACE_CLASS}>{` ${line.close}`}</span>
      </button>
      {trailComma}
    </>
  );
});

// Generic measured-height windowed list. Renders only the items within the
// scroll viewport (± overscan). The windowing core — robust scroll-parent
// detection, force-recompute, the viewportH===0 guard, and the self-scroll
// fallback — is documented on the RAW_* constants above; it exists because the
// shared VirtualFolderList locked onto a full range in this panel's scroll
// context. `getKey` must be stable per item so the height cache and React keys
// agree; `renderRow(item, index)` draws one row's content (the wrapping row
// <div> with the measure ref is supplied here).
export const VirtualList = forwardRef(function VirtualList({ items, getKey, renderRow, estRowHeight = EST_ROW_H, overscanPx = OVERSCAN_PX, className = "", rowClassName = "", anchorPrepend = false }, ref) {
  const count = items.length;
  const containerRef = useRef(null);
  const heightsRef = useRef(new Map()); // measured row heights, keyed by item key
  const rafRef = useRef(0);
  const [scrollEl, setScrollEl] = useState(null);
  const [ownScroll, setOwnScroll] = useState(false);
  // Mirror scrollEl / ownScroll into refs so the imperative scrollToIndex
  // handle can read the resolved surface without being rebuilt on every state
  // change (and without the parent needing the scroll element).
  const scrollElRef = useRef(null);
  scrollElRef.current = scrollEl;
  const ownScrollRef = useRef(false);
  ownScrollRef.current = ownScroll;
  // Bootstrap window: render a small bounded slice on first paint (never every
  // row), with an estimated bottom spacer so the scrollbar is roughly faithful
  // until the real window is computed.
  const [win, setWin] = useState(() => {
    const end = Math.min(count, BOOTSTRAP_ROWS);
    return { start: 0, end, topH: 0, bottomH: Math.max(0, count - end) * estRowHeight };
  });
  const winRef = useRef(win);
  winRef.current = win;

  const keys = useMemo(() => items.map((it, i) => String(getKey(it, i))), [items, getKey]);
  const heightOf = useCallback((i) => heightsRef.current.get(keys[i]) ?? estRowHeight, [keys, estRowHeight]);

  const metrics = useCallback(() => {
    const container = containerRef.current;
    if (!scrollEl || !container) return null;
    if (ownScroll) {
      return { scrollTop: scrollEl.scrollTop, baselineTop: 0, viewportH: scrollEl.clientHeight, count, heightOf, overscanPx };
    }
    const sRect = scrollEl.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    return { scrollTop: -(cRect.top - sRect.top), baselineTop: 0, viewportH: sRect.height, count, heightOf, overscanPx };
  }, [scrollEl, ownScroll, count, heightOf, overscanPx]);

  // Spacer heights for an explicit [start, end). Used by the landing pin
  // below, which chooses its own range rather than deriving one from scroll.
  const windowFor = useCallback((start, end) => {
    let topH = 0;
    for (let i = 0; i < start; i++) topH += heightOf(i);
    let bottomH = 0;
    for (let i = end; i < count; i++) bottomH += heightOf(i);
    return { start, end, topH, bottomH };
  }, [count, heightOf]);

  // While a landing is in flight this holds the row index it is aiming at, and
  // the window is that row's neighbourhood instead of the scroll-derived one.
  // Without it the two drive each other in a circle: the landing scrolls from
  // an estimate, the scroll remounts a different window, those rows measure,
  // the estimate moves, and it oscillates without ever mounting the target.
  // Pinned, the target is in the DOM on the next frame and can be aligned by
  // its real rect, which is the only measurement that is not a guess.
  const pinRef = useRef(null);

  // A prepend anchor in flight -- see the anchorPrepend effects below. While
  // its window is still to land, every other window recompute stands down, so
  // the window committed for the shifted position is the one that commits.
  // It outranks the landing pin: the pin re-asks for its window every frame it
  // cannot find the row, so it recovers once the anchor clears.
  const anchorRef = useRef(null);

  // Always a fresh computeWindow (never reconcile) so a once-full range can't
  // lock; viewportH<=0 is "not measured yet" → keep the current window.
  const recompute = useCallback(() => {
    if (anchorRef.current?.win) return;
    const m = metrics();
    if (!m || m.viewportH <= 0) return;
    const pin = pinRef.current;
    const next = pin == null
      ? computeWindow(m)
      : windowFor(Math.max(0, pin - 3), Math.min(count, pin + 4));
    if (!windowsEqual(winRef.current, next)) {
      winRef.current = next;
      setWin(next);
    }
  }, [metrics, windowFor, count]);

  // Read the latest recompute from a ref so the row observer + measure callback
  // can be created ONCE. If they were rebuilt on every recompute identity change
  // (e.g. right after scrollEl resolves on mount), the rebuild would disconnect
  // the observer that already-mounted rows were attached to, leaving them
  // unobserved — late wrap / resize / font-swap height changes would then stop
  // triggering a recompute (O-1958 review).
  const recomputeRef = useRef(recompute);
  recomputeRef.current = recompute;

  // Resolve the scroll surface once mounted; fall back to self-scroll.
  useLayoutEffect(() => {
    const parent = findScrollParent(containerRef.current);
    if (parent) {
      setScrollEl(parent);
      setOwnScroll(false);
    } else {
      setScrollEl(containerRef.current);
      setOwnScroll(true);
    }
  }, []);

  // Scroll + viewport-resize wiring. The ResizeObserver on the scroll element
  // is load-bearing: OverlayScrollbars sizes its viewport on a deferred rAF, so
  // the 0→real-height transition arrives here and triggers the first real
  // window.
  useEffect(() => {
    if (!scrollEl) return undefined;
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        recompute();
      });
    };
    recompute();
    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => recompute()) : null;
    ro?.observe(scrollEl);
    return () => {
      scrollEl.removeEventListener("scroll", onScroll);
      ro?.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, [scrollEl, recompute]);

  // Recompute when the item set changes (data swap, collapse/expand).
  useEffect(() => {
    recompute();
  }, [keys, recompute]);

  // Items prepended above the row being read (a page of the record landing in
  // front of the axis) would push it down by their height; with anchorPrepend
  // the scroll moves so that row stays where the reader's eye is. The anchor
  // is the row at the top of the viewport and its offset there, both taken
  // from the heights known before the items changed; the scroll then follows
  // the row's position under the new items. Unmounted rows count at the
  // estimate -- the figure the spacers use -- and each commit that mounts rows
  // above the anchor replaces estimates with measurements, so the position is
  // re-read and corrected over a few commits until it holds. Only a prepend
  // qualifies (the old first key reappears at k with everything after it
  // intact -- not a reorder or a filter), and only while the list's top is
  // scrolled above the viewport: a reader looking at the control above the
  // list sees the new rows fill in below it instead.
  //
  // Two phases, because the spacers are still sized for the old items when the
  // first effect runs: a scroll set then would clamp at the old extent. A
  // window over the new items is committed first (synchronously, before
  // paint); the effect on that window moves the scroll, recomputes for where
  // it actually is, and settles. It keys on the window alone and checks it is
  // the one committed here: keyed on anything that changes in the same commit
  // as the items, it would fire against the old spacers.
  const prevKeysRef = useRef(keys);
  useLayoutEffect(() => {
    const prev = prevKeysRef.current;
    prevKeysRef.current = keys;
    if (!anchorPrepend || prev === keys || !prev.length || keys.length <= prev.length) return;
    const k = keys.length - prev.length;
    if (keys[k] !== prev[0] || keys[keys.length - 1] !== prev[prev.length - 1]) return;
    const m = metrics();
    if (!scrollElRef.current || !m || m.scrollTop <= 0) return;
    const heightOfPrev = (i) => heightsRef.current.get(prev[i]) ?? estRowHeight;
    // The row under the viewport's top edge, by the heights the old items had.
    let above = 0;
    let row = prev.length - 1;
    for (let i = 0; i < prev.length; i++) {
      const h = heightOfPrev(i);
      if (above + h > m.scrollTop) { row = i; break; }
      above += h;
    }
    const anchor = { index: row + k, offset: above - m.scrollTop, passes: 0, win: null };
    let target = -anchor.offset;
    for (let i = 0; i < anchor.index; i++) target += heightOf(i);
    // A fresh object even when the geometry matches: the shift rides on this
    // commit, and the effect below recognises the window by identity.
    anchor.win = { ...computeWindow({ ...m, scrollTop: target }) };
    anchorRef.current = anchor;
    winRef.current = anchor.win;
    setWin(anchor.win);
  }, [keys, anchorPrepend, metrics, heightOf, estRowHeight]);
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    if (anchor.win) {
      if (win !== anchor.win) return;
      anchor.win = null;
    }
    const el = scrollElRef.current;
    const m = metrics();
    if (!el || !m) { anchorRef.current = null; return; }
    let target = -anchor.offset;
    for (let i = 0; i < anchor.index; i++) target += heightOf(i);
    const delta = target - m.scrollTop;
    if (Math.abs(delta) < 0.5 || anchor.passes >= 4) { anchorRef.current = null; return; }
    anchor.passes += 1;
    el.scrollTop += delta;
    const before = winRef.current;
    recomputeRef.current();
    // No new window means no new measurements to settle against.
    if (winRef.current === before) anchorRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per committed window; metrics/heightOf are read fresh
  }, [win]);

  // Per-row height measurement → recompute so spacers track variable / wrapped
  // row heights.
  const rowObsRef = useRef(null);
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      let changed = false;
      for (const e of entries) {
        const key = e.target.dataset?.vrow;
        if (!key) continue;
        const h = e.target.getBoundingClientRect().height;
        if (h > 0 && heightsRef.current.get(key) !== h) {
          heightsRef.current.set(key, h);
          changed = true;
        }
      }
      if (changed) recomputeRef.current();
    });
    rowObsRef.current = ro;
    return () => {
      ro.disconnect();
      rowObsRef.current = null;
    };
  }, []);

  const measureRow = useCallback((node) => {
    if (!node) return;
    const key = node.dataset?.vrow;
    if (!key) return;
    const h = node.getBoundingClientRect().height;
    if (h > 0 && heightsRef.current.get(key) !== h) {
      heightsRef.current.set(key, h);
      recomputeRef.current();
    }
    rowObsRef.current?.observe(node);
  }, []);

  // Scroll-space top (px) of display row `idx` — sums measured/estimated
  // heights of the rows before it. Compute-only: the caller (SignalLocator)
  // drives its own eased scroll off this value and re-reads it each frame, so
  // the glide self-corrects as virtualized rows mount and their real heights
  // replace the estimate. Returns null until the scroll surface is resolved.
  const topForIndex = useCallback((idx) => {
    const el = scrollElRef.current;
    const container = containerRef.current;
    if (!el || !container || idx == null || idx < 0 || idx >= count) return null;
    let before = 0;
    for (let i = 0; i < idx; i++) before += heightOf(i);
    if (ownScrollRef.current) return before;
    const sRect = el.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    return el.scrollTop + (cRect.top - sRect.top) + before;
  }, [count, heightOf]);

  // Land display row `idx` in the scroll viewport, and keep aiming until it
  // settles. The list does this itself because it is the only holder of the
  // height model: an outside caller can only estimate, and an estimate for a
  // virtualized list is mostly unmeasured rows.
  //
  // `inset` is how much sticky chrome covers the top of the viewport;
  // align "center" parks the row in the middle of what is left below it.
  // Returns a cancel function.
  const scrollToIndex = useCallback((idx, { align = "top", inset = 0, budgetMs = 1200 } = {}) => {
    if (idx == null || idx < 0 || idx >= count) return () => {};
    if (typeof window === "undefined" || !window.requestAnimationFrame) return () => {};
    pinRef.current = idx;
    recomputeRef.current();
    let raf = 0;
    let start = null;
    let settled = 0;
    let taken = false;
    const release = () => {
      pinRef.current = null;
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
      const el = scrollElRef.current;
      if (el) for (const t of READER_TAKEOVER_EVENTS) el.removeEventListener(t, take);
      recomputeRef.current();
    };
    // The reader's own input ends the landing at once. Scroll position alone
    // cannot be the signal: the browser clamps scrollTop whenever measurement
    // shrinks the content, and reading that as intent aborts the landing
    // exactly when it is still converging.
    function take() { taken = true; release(); }
    const el0 = scrollElRef.current;
    if (el0) for (const t of READER_TAKEOVER_EVENTS) el0.addEventListener(t, take, { passive: true });
    const step = (now) => {
      if (taken) return;
      if (start == null) start = now;
      const viewport = scrollElRef.current;
      const row = containerRef.current?.querySelector(`[data-display-index="${idx}"]`);
      if (viewport && row) {
        const vr = viewport.getBoundingClientRect();
        const rr = row.getBoundingClientRect();
        const below = viewport.clientHeight - inset;
        const centre = align === "center" && rr.height < below ? (below - rr.height) / 2 : 0;
        const goal = Math.max(0, Math.min(
          viewport.scrollTop + (rr.top - vr.top) - inset - centre,
          viewport.scrollHeight - viewport.clientHeight,
        ));
        const moved = Math.abs(viewport.scrollTop - goal);
        viewport.scrollTop = goal;
        settled = moved < 1 ? settled + 1 : 0;
        if (settled >= 3) { release(); return; }
      } else {
        // Not mounted yet (or the window was rebuilt out from under us).
        recomputeRef.current();
      }
      if (now - start < budgetMs) raf = window.requestAnimationFrame(step);
      else release();
    };
    raf = window.requestAnimationFrame(step);
    return release;
  }, [count]);

  useImperativeHandle(ref, () => ({ topForIndex, scrollToIndex }), [topForIndex, scrollToIndex]);

  return (
    <div
      ref={containerRef}
      className={`${className}${ownScroll ? " overflow-auto" : ""}`}
      style={ownScroll ? { maxHeight: "60vh" } : undefined}
    >
      <div style={{ height: win.topH }} aria-hidden="true" />
      {items.slice(win.start, win.end).map((item, i) => {
        const index = win.start + i;
        return (
          <div key={keys[index]} ref={measureRow} data-vrow={keys[index]} data-display-index={index} className={rowClassName}>
            {renderRow(item, index)}
          </div>
        );
      })}
      <div style={{ height: win.bottomH }} aria-hidden="true" />
    </div>
  );
});

// Floating locator for the signal highlights inside a trace's turn history.
// The marked span that fired a signal is easy to lose in a long, virtualized
// history — this pins a small pill to the bottom of the scroll viewport that
// jumps to (and flashes) each matched span, cycles through multiple hits, and
// shows a ↑/↓ cue when the current hit has scrolled off-screen. On open it
// auto-jumps to the first hit so the panel lands on the evidence, not the top.
//
// `hits` is [{ turnIndex, ns, accent }] (one per turn that fired), ordered by
// turn; `order`/`totalTurns` map a real turn index to its display-row index in
// the VirtualList; `listRef` is that list's imperative handle; `autoKey`
// changes per trace to re-arm the auto-jump.
// Exported for the auto-scroll gate test (drives autoScroll true/false).
export function SignalLocator({ hits, order, totalTurns, listRef, autoKey, autoScroll = false, onFocusTurn = null }) {
  const [active, setActive] = useState(0);
  const [dir, setDir] = useState("here");
  const rootRef = useRef(null);
  const activeRef = useRef(0);
  activeRef.current = active;
  const animRef = useRef(0);
  useEffect(() => () => { if (animRef.current) cancelAnimationFrame(animRef.current); }, []);
  // Post-settle hold: the glide leaves the mark centered, but content above it
  // can still grow AFTER the animation ends — the async Related Work / triage
  // panels land their rows, virtualized rows re-measure — shifting the evidence
  // away without any scroll event. Record where the mark settled
  // (viewport-relative top) and compensate scrollTop when layout moves it,
  // until the reader scrolls or interacts — the hold must never fight a human.
  const anchorRef = useRef(null); // { realIndex, top } — armed at settle
  const ownScrollRef = useRef(null); // last scrollTop WE wrote, to tell our scrolls from the reader's

  const writeScrollTop = useCallback((viewport, top) => {
    ownScrollRef.current = top;
    viewport.scrollTop = top;
  }, []);

  const armAnchor = useCallback((realIndex, viewport) => {
    const turnEl = document.querySelector(`[data-turn-index="${realIndex}"]`);
    const mark = turnEl && turnEl.querySelector("mark[data-signal-mark]");
    anchorRef.current = mark
      ? { realIndex, top: mark.getBoundingClientRect().top - viewport.getBoundingClientRect().top }
      : null;
  }, []);

  const displayIndexOf = useCallback(
    (realIndex) => (order === "latest" ? totalTurns - 1 - realIndex : realIndex),
    [order, totalTurns],
  );

  const flashTurn = useCallback((realIndex) => {
    const turnEl = document.querySelector(`[data-turn-index="${realIndex}"]`);
    const marks = turnEl ? turnEl.querySelectorAll("mark[data-signal-mark]") : null;
    if (!marks || !marks.length) return;
    marks.forEach((m) => {
      m.classList.remove("signal-mark-flash");
      void m.offsetWidth; // restart the animation on a repeat jump to the same span
      m.classList.add("signal-mark-flash");
    });
    window.setTimeout(() => marks.forEach((m) => m.classList.remove("signal-mark-flash")), 1600);
  }, []);

  const focus = useCallback((i) => {
    if (!hits.length) return;
    const clamped = ((i % hits.length) + hits.length) % hits.length;
    setActive(clamped);
    const realIndex = hits[clamped].turnIndex;
    // Landing on a signal hit — the drilldown auto-scroll or a prev/next step —
    // is the reader saying "this one", so it sets the shared coordinate too.
    // Before the early return below: the turn is chosen even if the glide
    // cannot run because the scroll surface is not resolved yet.
    onFocusTurn?.(realIndex);
    const viewport = findScrollParent(rootRef.current);
    if (!viewport) return;
    if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = 0; }
    anchorRef.current = null; // re-armed at this glide's settle
    const displayIdx = displayIndexOf(realIndex);

    // Where scrollTop should end up. Once the target turn's mark is mounted we
    // center on its real rect (`precise`); until then — the row may be
    // virtualized far out of the DOM — we aim at the list's height estimate so
    // the glide starts immediately and refines as rows mount underneath it.
    const aim = () => {
      const turnEl = document.querySelector(`[data-turn-index="${realIndex}"]`);
      const mark = turnEl && turnEl.querySelector("mark[data-signal-mark]");
      if (mark) {
        const vr = viewport.getBoundingClientRect();
        const mr = mark.getBoundingClientRect();
        return { top: viewport.scrollTop + (mr.top - vr.top) - (viewport.clientHeight - mr.height) / 2, precise: true };
      }
      const rowTop = listRef.current?.topForIndex?.(displayIdx);
      if (rowTop == null) return { top: viewport.scrollTop, precise: false };
      return { top: rowTop - viewport.clientHeight * 0.38, precise: false };
    };
    const clamp = (t) => Math.max(0, Math.min(t, viewport.scrollHeight - viewport.clientHeight));

    // Reduced motion: jump, let the row mount a frame, snap precise, flash.
    if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      writeScrollTop(viewport, clamp(aim().top));
      window.requestAnimationFrame(() => {
        writeScrollTop(viewport, clamp(aim().top));
        window.requestAnimationFrame(() => { armAnchor(realIndex, viewport); flashTurn(realIndex); });
      });
      return;
    }

    // One continuous ease-out that re-targets every frame: move a fixed
    // fraction of the remaining distance toward the (possibly still-refining)
    // goal. No instant pre-jump, no second native animation to stitch to —
    // scrolling itself mounts the intervening rows. Cap so a moving target
    // can't loop forever.
    const start = performance.now();
    let settled = 0;
    const step = (now) => {
      const { top, precise } = aim();
      const goal = clamp(top);
      const cur = viewport.scrollTop;
      const delta = goal - cur;
      writeScrollTop(viewport, Math.abs(delta) < 0.5 ? goal : cur + delta * 0.22);
      if (precise && Math.abs(delta) < 1) settled += 1; else settled = 0;
      if ((precise && settled >= 2) || now - start > 900) {
        writeScrollTop(viewport, goal);
        animRef.current = 0;
        armAnchor(realIndex, viewport);
        flashTurn(realIndex);
        return;
      }
      animRef.current = window.requestAnimationFrame(step);
    };
    animRef.current = window.requestAnimationFrame(step);
  }, [hits, displayIndexOf, listRef, flashTurn, writeScrollTop, armAnchor, onFocusTurn]);

  const focusRef = useRef(focus);
  focusRef.current = focus;

  // Auto-jump to the first hit when the trace (autoKey) changes — ONLY when the
  // trace was opened from a signals drilldown (autoScroll). Otherwise (browsing
  // session history, the trace log, etc.) we don't bounce the reader to a signal
  // span — the pill still shows the count and prev/next still work on demand.
  // Double rAF so the VirtualList has resolved its scroll surface and measured
  // its window first — and so it runs after ContextPanel's scroll-position restore.
  useEffect(() => {
    if (!hits.length) return undefined;
    setActive(0);
    anchorRef.current = null; // turn indices mean something else in the new trace
    if (!autoScroll) return undefined;
    let r1 = 0;
    let r2 = 0;
    r1 = window.requestAnimationFrame(() => {
      r2 = window.requestAnimationFrame(() => focusRef.current(0));
    });
    return () => { window.cancelAnimationFrame(r1); window.cancelAnimationFrame(r2); };
  }, [autoKey, hits.length, autoScroll]);

  // The hold itself. A ResizeObserver on the shared panel content (this
  // locator's parent grid holds the metadata panels AND the turn list, so any
  // late-loading block that can shift the mark resizes it) re-reads the armed
  // mark's drift from its settle position and scrolls it back. Disarm on any
  // scroll we didn't write ourselves — wheel/scrollbar/keyboard all surface
  // there — plus raw input events, which catch reader intent even when the
  // viewport is already at a scroll limit.
  useEffect(() => {
    const viewport = findScrollParent(rootRef.current);
    const content = rootRef.current?.parentElement;
    if (!viewport || !hits.length) return undefined;
    const disarm = () => { anchorRef.current = null; };
    const compensate = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const turnEl = document.querySelector(`[data-turn-index="${anchor.realIndex}"]`);
      const mark = turnEl && turnEl.querySelector("mark[data-signal-mark]");
      if (!mark) return; // virtualized out — nothing measurable to hold
      const drift = mark.getBoundingClientRect().top - viewport.getBoundingClientRect().top - anchor.top;
      if (Math.abs(drift) < 1) return;
      const goal = Math.max(0, Math.min(viewport.scrollTop + drift, viewport.scrollHeight - viewport.clientHeight));
      ownScrollRef.current = goal;
      viewport.scrollTop = goal;
    };
    const onScroll = () => {
      if (!anchorRef.current) return;
      if (ownScrollRef.current == null || Math.abs(viewport.scrollTop - ownScrollRef.current) > 1.5) disarm();
    };
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(compensate) : null;
    if (content) ro?.observe(content);
    viewport.addEventListener("scroll", onScroll, { passive: true });
    const inputEvents = ["wheel", "touchstart", "keydown", "pointerdown"];
    inputEvents.forEach((t) => viewport.addEventListener(t, disarm, { passive: true }));
    return () => {
      ro?.disconnect();
      viewport.removeEventListener("scroll", onScroll);
      inputEvents.forEach((t) => viewport.removeEventListener(t, disarm));
    };
  }, [hits.length]);

  // Track whether the active hit is above / below / within the viewport so the
  // pill can point the way. Geometric when the row is mounted; falls back to
  // comparing display-row indices against the rendered window when it's been
  // virtualized out of the DOM entirely.
  useEffect(() => {
    const viewport = findScrollParent(rootRef.current);
    if (!viewport || !hits.length) return undefined;
    let raf = 0;
    const compute = () => {
      raf = 0;
      const hit = hits[activeRef.current];
      if (!hit) { setDir("here"); return; }
      const vr = viewport.getBoundingClientRect();
      const turnEl = document.querySelector(`[data-turn-index="${hit.turnIndex}"]`);
      if (turnEl) {
        const r = turnEl.getBoundingClientRect();
        if (r.bottom < vr.top + 8) setDir("up");
        else if (r.top > vr.bottom - 8) setDir("down");
        else setDir("here");
        return;
      }
      const targetDisp = displayIndexOf(hit.turnIndex);
      let min = Infinity;
      let max = -Infinity;
      viewport.querySelectorAll("[data-display-index]").forEach((n) => {
        const d = Number(n.dataset.displayIndex);
        if (Number.isFinite(d)) { if (d < min) min = d; if (d > max) max = d; }
      });
      if (targetDisp < min) setDir("up");
      else if (targetDisp > max) setDir("down");
      else setDir("here");
    };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(compute); };
    compute();
    viewport.addEventListener("scroll", onScroll, { passive: true });
    // Late content growth (Related Work rows landing) moves the hit without
    // any scroll event — recompute on content resize too so the arrow stays
    // honest when the post-settle hold isn't armed.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onScroll) : null;
    if (rootRef.current?.parentElement) ro?.observe(rootRef.current.parentElement);
    return () => { ro?.disconnect(); viewport.removeEventListener("scroll", onScroll); if (raf) window.cancelAnimationFrame(raf); };
  }, [hits, displayIndexOf, active]);

  if (!hits.length) return null;
  const accent = hits[active]?.accent || "var(--green)";
  const multi = hits.length > 1;
  // Every stop from a hand-off follow and none from a fired signal: the pill
  // must not claim "signals" for spans that are shared content.
  const allJoin = hits.every((h) => h.ns === "join");
  const arrow = dir === "up" ? "M6 8l3-3 3 3" : dir === "down" ? "M6 4l3 3 3-3" : null;

  return (
    <div ref={rootRef} data-signal-locator="" className="sticky bottom-[10px] z-[3] -mt-[6px] flex justify-end pr-[9px] pointer-events-none">
      <div
        className="pointer-events-auto inline-flex items-center gap-[2px] rounded-[7px] border bg-[color:var(--bg)] py-[3px] pl-[7px] pr-[4px] shadow-[0_2px_10px_rgba(0,0,0,0.28)]"
        style={{ borderColor: `color-mix(in srgb, ${accent} 55%, var(--line))` }}
      >
        <button
          type="button"
          onClick={() => focus(active)}
          title={dir === "here" ? "Jump to the highlighted signal span" : "Scroll back to the highlighted signal span"}
          className="inline-flex items-center gap-[5px] cursor-pointer text-[color:var(--ink)]"
        >
          {/* flag glyph in the hit's accent */}
          <svg viewBox="0 0 12 12" className="h-[11px] w-[11px] shrink-0" fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 1.5V11" />
            <path d="M3 2h6l-1.4 2L9 6H3" fill={accent} stroke="none" />
          </svg>
          <span className="text-[12px] leading-[1.08] whitespace-nowrap">
            {multi
              ? `${active + 1}/${hits.length} ${allJoin ? "shared spans" : "signals"}`
              : allJoin ? "Shared span" : "Signal span"}
          </span>
          {arrow && (
            <svg viewBox="0 0 12 12" className="h-[11px] w-[11px] shrink-0 animate-pulse" fill="none" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d={arrow} />
            </svg>
          )}
        </button>
        {multi && (
          <span className="ml-[3px] inline-flex items-center border-l border-[color:var(--line)] pl-[3px]">
            <button
              type="button"
              onClick={() => focus(active - 1)}
              title="Previous signal span"
              aria-label="Previous signal span"
              className="grid h-[18px] w-[16px] place-items-center rounded-[5px] cursor-pointer text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors"
            >
              <svg viewBox="0 0 12 12" className="h-[9px] w-[9px]" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 2.5l-3.5 3.5L8 9.5" /></svg>
            </button>
            <button
              type="button"
              onClick={() => focus(active + 1)}
              title="Next signal span"
              aria-label="Next signal span"
              className="grid h-[18px] w-[16px] place-items-center rounded-[5px] cursor-pointer text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors"
            >
              <svg viewBox="0 0 12 12" className="h-[9px] w-[9px]" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 2.5l3.5 3.5L4 9.5" /></svg>
            </button>
          </span>
        )}
      </div>
    </div>
  );
}

function RawJsonVirtualized({ data }) {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const lines = useMemo(() => flattenJson(data, collapsed), [data, collapsed]);

  const toggle = useCallback((nid) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(nid)) next.delete(nid);
      else next.add(nid);
      return next;
    });
  }, []);

  const getKey = useCallback((line) => line.id, []);
  const renderRow = useCallback((line) => <JsonLine line={line} onToggle={toggle} />, [toggle]);

  return (
    <VirtualList
      items={lines}
      getKey={getKey}
      renderRow={renderRow}
      estRowHeight={EST_ROW_H}
      className={`m-0 max-w-full rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[8px] ${JSON_BASE}`}
      rowClassName="whitespace-pre-wrap [overflow-wrap:anywhere] [word-break:break-word]"
    />
  );
}

// ── Raw API payload builder ─────────────────────────────────────────

// Renders the raw, unparsed api_request blob exactly as stored in
// events.event_data.api_request. The blob comes through the API as a
// JSON string on `trace.apiRequestRaw` (see traces.js + apiClient.js).
// We only parse for the virtualized collapsible view; the underlying
// string is preserved for download. Falls back to a "no payload" hint
// when the field is empty (older API versions or trace records
// missing the join row).
function RawApiRequestView({ trace }) {
  const raw = trace?.apiRequestRaw || "";
  if (!raw) {
    return (
      <div className="grid gap-[4px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px] text-[11px] leading-[1.32] text-[color:var(--muted)]">
        Raw payload not available for this trace.
      </div>
    );
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    // Non-JSON or malformed — show the source verbatim so the operator
    // can still see what the upstream actually stored.
    return (
      <pre className="overflow-auto rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px] text-[11px] leading-[1.32] text-[color:var(--muted)] whitespace-pre-wrap break-all">
        {raw}
      </pre>
    );
  }
  return <RawJsonVirtualized data={parsed} />;
}

function downloadRawJson(trace) {
  const raw = trace?.apiRequestRaw || "";
  // Pretty-print on the way out for human readability, but only if it
  // parses cleanly. Otherwise download the source verbatim — preserves
  // whatever the upstream stored even when malformed.
  let body = raw;
  try {
    body = JSON.stringify(JSON.parse(raw), null, 2);
  } catch { /* keep as-is */ }
  const blob = new Blob([body], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${trace.traceId}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Reusable sub-blocks shared across the layout variants ─────────

// SessionOriginCard — tooltip body for the session chip. Destructures the
// session's originating agent process (resolved via the AI endpoint-stats RPC
// into trace.sessionOrigin) into a compact labeled card: agent, OS user, and
// the launch command line. The command line can be enormous (a cmux-wrapped
// launch inlines its whole hooks config), so it's clamped to a few lines and
// the whole card is bounded — the tooltip is reference context, not a console.
function OriginRow({ label, children }) {
  return (
    <div className="grid items-start gap-[10px] min-w-0 [grid-template-columns:62px_minmax(0,1fr)]">
      <span className="whitespace-nowrap text-[12px] leading-[1.34] text-[color:var(--muted)]">{label}</span>
      <div className="min-w-0 text-[11px] leading-[1.34] text-[color:var(--ink)]">{children}</div>
    </div>
  );
}

function SessionOriginCard({ origin }) {
  const { imageName, commandLine, account, domain, provider } = origin;
  const agent = deriveAgentIdentity(undefined, commandLine, imageName, provider)?.name || "";
  const image = imageName ? imageName.split(/[\\/]/).pop() : "";
  // Windows reads DOMAIN\user; macOS domain is "local". Either side alone is fine.
  const user = account && domain ? `${domain}\\${account}` : (account || domain || "");
  return (
    <div className="grid gap-[5px] text-left min-w-0">
      <span className="text-[12px] leading-[1.4] text-[color:var(--muted)]">Trace origin</span>
      {(agent || image) && (
        <OriginRow label="Agent">
          {agent && <span className="font-medium">{agent}</span>}
          {image && <span className="text-[color:var(--muted)]">{agent ? `  ·  ${image}` : image}</span>}
        </OriginRow>
      )}
      {user && <OriginRow label="User"><span className="break-all">{user}</span></OriginRow>}
      {commandLine && (
        <OriginRow label="Command">
          {/* Collapse whitespace so a pretty-printed blob (cmux inlines its
              hooks JSON) reads as dense flowing text, then clamp to a handful
              of lines so the card can't balloon off-screen. */}
          <span className="block font-mono text-[10px] leading-[1.36] break-all overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:6]">
            {commandLine.replace(/\s+/g, " ").trim()}
          </span>
        </OriginRow>
      )}
    </div>
  );
}

// TraceIdentityStrip - which computer, whose login, which tool, as the
// trace's byline. It rides in the right panel's header under the "Trace
// Details" title (ScopedView's contextByline slot), so it names the trace in
// every reading rather than only one, and costs the timeline no row. The
// first questions a reviewer asks ("which machine, who was logged in, was
// this the CLI or the desktop app") used to need a detour through the
// endpoint and session views. The hostname comes from the endpoint label
// cache, the login from the OS account that launched the session's agent
// process (the endpoint's registered user stands in when no launch was
// observed, and says so), and the tooling from deriveTraceTooling, which
// shows its evidence on hover.
//
// ONE line, always. A byline that wrapped left the last fact orphaned on its
// own line behind a dangling separator, and one that clipped hid the facts it
// exists to show. So the strip measures its facts against the width it has
// and shows the ones that fit, dropping from the least identifying end:
// session and model go first, then the timestamp; the machine, the login and
// the tooling go last. The facts are measured as plain text in a hidden
// twin of the row, so the interactive elements exist once.
const BYLINE_LINK_CLASS = "inline cursor-pointer p-0 text-[color:var(--ink)] underline decoration-dotted underline-offset-2 decoration-[color:var(--line-strong)] transition-colors hover:text-[color:var(--green)]";
const BYLINE_SEPARATOR_CLASS = "mx-[6px] text-[color:var(--line-strong)]";

// Windows accounts read DOMAIN\user; macOS reports the domain as "local",
// which adds nothing to the name.
export function formatLogin(account, domain) {
  const a = String(account || "").trim();
  const d = String(domain || "").trim();
  if (!a) return d;
  if (!d || d.toLowerCase() === "local") return a;
  return `${d}\\${a}`;
}

// Which facts fit in `width`, in priority order (lower first), each fact
// paying for its separator once another fact is already in. The first fact
// that does not fit ends the byline: skipping past it to a narrower, less
// identifying fact behind it showed a session id while hiding the model, and
// made what is shown depend on string lengths rather than on what matters.
// Returns the set of indexes into `widths`. The highest-priority fact is
// always kept: an empty byline says less than a clipped one.
export function fitBylineFacts(widths, priorities, separatorWidth, width) {
  const order = widths.map((_, i) => i).sort((a, b) => priorities[a] - priorities[b]);
  const keep = new Set();
  let used = 0;
  for (const i of order) {
    const cost = widths[i] + (keep.size ? separatorWidth : 0);
    if (keep.size && used + cost > width) break;
    used += cost;
    keep.add(i);
  }
  return keep;
}

// The readings a trace has. A `hist` value outside this set (a link to the
// retired Tree reading, a typo) lands on Context rather than on whichever
// branch the render chain ends in.
const TRACE_READINGS = ["turns", "work", "lanes", "raw"];

// The reading the detail panel actually renders for a `hist` URL value on
// this trace. A trace with nothing to lay out (no turns and no offered tools)
// falls back to the raw request whatever the URL asks for, and "raw" needs a
// stored body. Shared with the loading skeleton, which outlines the reading
// the panel will open in.
export function effectiveTraceReading(trace, hist) {
  const hasTurns = (trace?.turns?.length || 0) > 0 || (trace?.toolDefs?.length || 0) > 0;
  if (!hasTurns) return "raw";
  const view = hist && TRACE_READINGS.includes(hist) ? hist : "turns";
  if (view === "raw" && !trace?.apiRequestRaw) return "turns";
  return view;
}

// What the page hands ScopedView's header for a trace tab. Present only while
// the header is the trace's (no lens or pinned cell owns the title), and for
// EVERY reading of it: the byline stands where the panel's eyebrow and title
// stood, so the header keeps one height as the reader moves between readings
// (a header that grew for one reading shifted the plane, found live). The
// byline's pivots append to the current scope path, the same move the panel's
// own chips make. The seed row already knows the endpoint, model and session,
// so the byline appears with the tab and fills in login and tooling when the
// detail lands.
export function traceHeaderByline({ trace, headerOwnedByTrace = true, scopePath = [], onScopePathChange }) {
  if (!headerOwnedByTrace || !trace) return null;
  return (
    <TraceIdentityStrip
      trace={trace}
      turns={trace.turns || null}
      onScopeNavigate={(dim, id) => onScopePathChange([...scopePath, { dim, id }])}
    />
  );
}

export function TraceIdentityStrip({ trace, turns = null, onScopeNavigate = null }) {
  const endpointId = trace?.endpointId || null;
  const endpoint = useDimensionEntry(endpointId ? "endpoints" : null, endpointId);
  const hostname = endpoint.label && endpoint.label !== String(endpointId)
    ? endpoint.label
    : (trace?.endpointName || endpointId || "");
  const registeredUser = endpoint.meta?.registeredUser || "";
  const origin = trace?.sessionOrigin || null;
  const login = origin ? formatLogin(origin.account, origin.domain) : "";
  // The browser family the trace's browser observations share; "browser"
  // when they come from more than one.
  const browserFamilies = [...new Set((trace?.sourceObservations || [])
    .map((observation) => observation?.browserFamily)
    .filter(Boolean))];
  const browserFamily = browserFamilies.length > 1 ? "browser" : (browserFamilies[0] || "");
  const tooling = useMemo(
    () => deriveTraceTooling({
      turns,
      tools: trace?.tools,
      toolDefs: trace?.toolDefs,
      sessionOrigin: origin,
      provider: trace?.provider,
      clientSurface: trace?.clientSurface,
      browserFamily,
    }),
    [turns, trace?.tools, trace?.toolDefs, origin, trace?.provider, trace?.clientSurface, browserFamily],
  );
  const link = (dim, id, label) => (
    onScopeNavigate ? (
      <button type="button" onClick={() => onScopeNavigate(dim, id)} className={BYLINE_LINK_CLASS}>{label}</button>
    ) : (
      <span className="text-[color:var(--ink)]">{label}</span>
    )
  );
  // The separator between facts is a dot, so the tooling fact must not use
  // one inside itself: "Claude Code (CLI)" reads as one fact where the dotted
  // form read as two. The tooltip keeps the full label.
  const toolingQualifiers = [tooling.surface, tooling.ide ? "IDE attached" : null].filter(Boolean);
  const toolingText = tooling.product
    ? `${tooling.product}${tooling.version ? ` ${tooling.version}` : ""}${toolingQualifiers.length ? ` (${toolingQualifiers.join(", ")})` : ""}`
    : "";
  const surfaceNote = " (surface not captured)";

  // Display order is left to right; priority is what survives a narrow panel.
  // The date survives first: the header is the one place on the screen that
  // states it, and the machine, login and tool each appear again below in
  // the metadata rail (a narrow column showed the login and lost the day,
  // found live).
  const facts = [];
  if (trace?.timestamp) {
    const text = fullTimestampFormatter.format(trace.timestamp);
    facts.push({ key: "when", priority: 1, text, node: <span>{text}</span> });
  }
  if (endpointId) {
    facts.push({ key: "endpoint", priority: 2, text: hostname, node: link("endpoints", endpointId, hostname) });
  }
  if (login) {
    facts.push({
      key: "user", priority: 3, text: login,
      node: (
        <HoverTooltip placement="bottom" label="The OS account that launched this trace's agent process.">
          <span tabIndex={0} className="cursor-help text-[color:var(--ink)]">{login}</span>
        </HoverTooltip>
      ),
    });
  } else if (registeredUser) {
    facts.push({
      key: "user", priority: 3, text: `registered user ${registeredUser}`,
      node: (
        <HoverTooltip
          placement="bottom"
          wide
          label="The endpoint's registered user. The account that launched this trace was not observed, so this is the machine's owner rather than a login."
        >
          <span tabIndex={0} className="cursor-help">
            <span className="text-[color:var(--muted-soft)]">registered user </span>
            <span className="text-[color:var(--ink)]">{registeredUser}</span>
          </span>
        </HoverTooltip>
      ),
    });
  }
  if (tooling.product) {
    facts.push({
      key: "tooling", priority: 4, text: toolingText + (tooling.surface ? "" : surfaceNote),
      node: (
        <HoverTooltip
          placement="bottom"
          wide
          label={
            <span className="grid gap-[4px] text-left">
              <span className="font-medium">{tooling.label}</span>
              {tooling.evidence.map((e, i) => (
                <span key={i} className="text-[color:var(--muted)]">
                  <span className="text-[color:var(--muted-soft)]">{e.source}: </span>
                  <span className="break-all">{e.detail}</span>
                </span>
              ))}
              {!tooling.surface ? (
                <span className="text-[color:var(--muted)]">
                  Which surface ran it was not captured: the request carried no system prompt or billing header, and no launch process was observed.
                </span>
              ) : null}
            </span>
          }
        >
          <span tabIndex={0} className="cursor-help">
            <span data-trace-tooling="" className="text-[color:var(--ink)]">{toolingText}</span>
            {!tooling.surface ? <span className="text-[color:var(--muted-soft)]">{surfaceNote}</span> : null}
          </span>
        </HoverTooltip>
      ),
    });
  }
  if (trace?.model) {
    facts.push({ key: "model", priority: 5, text: trace.model, node: link("models", trace.model, trace.model) });
  }
  if (trace?.sessionId) {
    const label = trace.sessionTitle || trace.sessionId;
    facts.push({ key: "session", priority: 6, text: label, node: link("sessions", trace.sessionId, label) });
  }

  // Fit-to-width. Widths come from the hidden twin (plain text in the same
  // type), re-measured whenever the texts or the container's width change.
  // Before the first measurement, and wherever layout cannot be measured,
  // every fact shows.
  const rootRef = useRef(null);
  const twinRef = useRef(null);
  const signature = facts.map((f) => `${f.key}=${f.text}`).join("|");
  const [shown, setShown] = useState(null);
  // The width of EVERY fact, which is what the strip asks its flex row for.
  // Sized by its visible content instead, the strip ratcheted: a fact dropped
  // under pressure shrank the box, the box kept the fact out, and a wide
  // header ended up showing the date alone (found live).
  const [fullW, setFullW] = useState(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    const twin = twinRef.current;
    if (!root || !twin) return undefined;
    const fit = () => {
      const width = root.clientWidth;
      if (!width) return;
      const nodes = [...twin.children];
      const separator = nodes.pop();
      const widths = nodes.map((n) => n.getBoundingClientRect().width);
      const priorities = nodes.map((n) => Number(n.dataset.priority));
      // A separator costs its glyph AND its margins; the rect alone under-
      // counted by 12px a dot, which is how the last fact ended up cropped.
      const sepStyle = getComputedStyle(separator);
      const separatorWidth = separator.getBoundingClientRect().width
        + (parseFloat(sepStyle.marginLeft) || 0) + (parseFloat(sepStyle.marginRight) || 0);
      // A few pixels of slack for sub-pixel rounding between the twin and
      // the live row; the ellipsis on the row is the backstop, not the plan.
      const keep = fitBylineFacts(widths, priorities, separatorWidth, width - 4);
      const next = nodes.filter((_, i) => keep.has(i)).map((n) => n.dataset.key).join("|");
      setShown((cur) => (cur === next ? cur : next));
      const full = Math.ceil(widths.reduce((a, w) => a + w, 0) + separatorWidth * Math.max(0, widths.length - 1)) + 4;
      setFullW((cur) => (cur === full ? cur : full));
    };
    fit();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(root);
    return () => ro.disconnect();
  }, [signature]);

  // With nothing to say yet (a deep-linked trace still loading knows only its
  // id), the strip still holds its place at the right edge, so the controls
  // after it stay there: the date and machine it is waiting for, as bars.
  if (!facts.length) {
    return (
      <span data-trace-identity-strip="" className="ml-auto flex min-w-0 shrink items-center gap-[10px]" aria-hidden="true">
        {trace?.placeholder ? [108, 92].map((w) => (
          <span key={w} className="h-[9px] rounded-[1px] bg-[color:var(--line-soft)] opacity-70" style={{ width: w }} />
        )) : null}
      </span>
    );
  }
  const shownKeys = shown == null ? null : new Set(shown.split("|"));
  const visible = shownKeys ? facts.filter((f) => shownKeys.has(f.key)) : facts;
  return (
    <span
      ref={rootRef}
      data-trace-identity-strip=""
      // A flex item of the header line it sits in: it asks for the width of
      // all its facts, gives way from there when the line is short, and sits
      // against the right edge when nothing else takes the room.
      className="relative ml-auto block min-w-0 shrink overflow-hidden text-ellipsis whitespace-nowrap text-right text-[11px] leading-[1.35] text-[color:var(--muted)]"
      style={fullW ? { flexBasis: `${fullW}px` } : undefined}
    >
      {visible.map((fact, i) => (
        <Fragment key={fact.key}>
          {i > 0 ? <span className={BYLINE_SEPARATOR_CLASS}>{"\u00b7"}</span> : null}
          {fact.node}
        </Fragment>
      ))}
      <span ref={twinRef} aria-hidden="true" className="invisible absolute left-0 top-0 whitespace-nowrap">
        {facts.map((fact) => (
          <span key={fact.key} data-key={fact.key} data-priority={fact.priority}>{fact.text}</span>
        ))}
        <span className={BYLINE_SEPARATOR_CLASS}>{"\u00b7"}</span>
      </span>
    </span>
  );
}

// The trace's place in the world as ONE breadcrumb — endpoint › topic
// chain › session — instead of separate Identity/Endpoint rows. The cluster
// crumb is the ancestry chain when assigned (already a hierarchy), or the
// dashed status chip when the trace has no topic to point at.
function IdentityBreadcrumb({ trace, endpointLabel, onScopeNavigate }) {
  const crumbs = [];
  if (trace.endpointId) {
    crumbs.push([
      "endpoint",
      <ScopeChip
        bare
        dim="endpoints"
        label={endpointLabel}
        onClick={() => onScopeNavigate("endpoints", trace.endpointId)}
      />,
    ]);
  }
  if (trace.clusterStatus === "clustered" && trace.clusterAncestors?.length > 0) {
    for (const a of trace.clusterAncestors) {
      crumbs.push([
        `cluster-${a.id}`,
        <ScopeChip
          bare
          dim="clusters"
          label={a.name || a.id}
          onClick={() => onScopeNavigate("clusters", a.id)}
        />,
      ]);
    }
  } else if (trace.primaryClusterId) {
    crumbs.push([
      "cluster",
      <ScopeChip
        bare
        dim="clusters"
        label={trace.primaryClusterName || trace.primaryClusterId}
        onClick={() => onScopeNavigate("clusters", trace.primaryClusterId)}
      />,
    ]);
  } else if (trace.clusterStatus === "outlier" || trace.clusterStatus === "pending" || trace.clusterStatus === "filtered" || trace.clusterStatus === "scaffolding") {
    crumbs.push([
      "cluster-status",
      <HoverTooltip
        placement="top"
        wide
        label={trace.clusterStatus === "outlier"
          ? "This trace didn't fall into any nearby topic."
          : trace.clusterStatus === "filtered"
          ? "This captured frame contains no user prompt, so it cannot be assigned to a topic."
            : trace.clusterStatus === "scaffolding"
              ? "Infrastructure prompt from a tool loop, harness, or warmup. It is excluded from topic assignment."
              : "This trace hasn't been grouped into a topic yet."}
      >
        <span
          tabIndex={0}
          className="inline-flex min-w-0 min-h-[22px] max-w-full items-center gap-[5px] rounded-[6px] border border-dashed border-[color:var(--line-soft)] bg-transparent px-[6px] text-[11px] leading-[1.08] text-[color:var(--muted-soft)] cursor-help"
        >
          <span className="shrink-0 text-[11px] text-[color:var(--muted-soft)]">{DIM_LABELS.clusters}</span>
          <span className="truncate min-w-0">{trace.clusterStatus === "outlier" ? "Outlier" : trace.clusterStatus === "filtered" ? "Non-prompt" : trace.clusterStatus === "scaffolding" ? "Infrastructure" : "Pending"}</span>
        </span>
      </HoverTooltip>,
    ]);
  }
  if (trace.sessionId) {
    crumbs.push([
      "session",
      trace.sessionOrigin ? (
        <HoverTooltip placement="top" wide label={<SessionOriginCard origin={trace.sessionOrigin} />}>
          <span className="inline-flex min-w-0 max-w-full cursor-help">
            <ScopeChip
              bare
              dim="sessions"
              label={trace.sessionTitle || trace.sessionId}
              onClick={() => onScopeNavigate("sessions", trace.sessionId)}
            />
          </span>
        </HoverTooltip>
      ) : (
        <ScopeChip
          bare
          dim="sessions"
          label={trace.sessionTitle || trace.sessionId}
          onClick={() => onScopeNavigate("sessions", trace.sessionId)}
        />
      ),
    ]);
  }
  if (!crumbs.length) return null;
  // ONE row, always: the row never wraps — crumbs shrink and the chips
  // truncate internally. Cluster crumbs (the long slugs) carry a higher
  // shrink weight and every crumb keeps a readable minimum, so "endpoint"
  // never collapses to a bare tag just because a cluster name is long.
  return (
    <div className="flex items-center gap-[4px] min-w-0">
      {crumbs.map(([key, node], i) => (
        <span
          key={key}
          className={[
            "inline-flex min-w-0 max-w-full items-center gap-[4px]",
            key.startsWith("cluster") ? "shrink-[4] min-w-[80px]" : "min-w-[64px]",
          ].join(" ")}
        >
          {i > 0 && (
            <span aria-hidden="true" className="shrink-0 text-[11px] leading-[1.1] text-[color:var(--muted-soft)]">›</span>
          )}
          {node}
        </span>
      ))}
    </div>
  );
}

// Compact key-value row used by the merged-metadata block.
// All rows share the same `[label | value]` horizontal layout — chip
// clusters flow into the value column and wrap inside it instead of
// stacking under their label. That gives a single consistent
// row-height rhythm regardless of whether the value is one line of
// text, two chips, or eight wrapped tool pills. No row dividers —
// the 80px label gutter + uniform vertical gap is enough to read
// as a definition list without competing visually with the prompt
// card above.
function MetaRow({ label, value, children }) {
  if (!children && (value == null || value === "")) return null;
  return (
    <div className="grid items-start gap-[10px] min-w-0 [grid-template-columns:80px_minmax(0,1fr)]">
      <span className="pt-[1px] text-[12px] leading-[1.3] text-[color:var(--muted)]">{label}</span>
      <div className="min-w-0 text-[12px] leading-[1.3] text-[color:var(--ink)]">
        {children ?? value}
      </div>
    </div>
  );
}

function captureSourceLabel(source) {
  switch (source) {
    case "proxy": return "Proxy";
    case "otlp": return "OTLP logs";
    case "office": return "Office traces";
    case "compliance_sync": return "Anthropic (Claude)";
    case "hooks": return "Hooks";
    case "transcript": return "Transcript";
    case "copilot_sync": return "M365 Copilot";
    case "openai_sync": return "OpenAI (ChatGPT)";
    case "dashboard_chat": return "Origin chat";
    case "local_tap": return "Local loopback tap";
    case "perplexity_audit_log": return "Perplexity";
    case "browser_extension": return "Browser extension";
    case "langsmith_sync": return "LangSmith";
    case "otlp_traces": return "OTLP traces";
    case "vertex_sync": return "Vertex AI";
    case "litellm_sync": return "LiteLLM";
    case "microsoft_foundry_sync": return "Microsoft Foundry";
    case "host_artifact": return "Host artifact";
    case "analytics_api": return "Analytics API";
    default: return source;
  }
}

// Capture-source detail as an (i) next to the "Trace metadata" title — debug
// provenance an operator rarely needs, so it reads on demand instead of
// costing a metadata row. Same traceCaptureSourceEnabled() gate the old
// Capture row had.
export function captureLines(trace) {
  const lines = (Array.isArray(trace.captureSources) ? trace.captureSources.filter(Boolean) : [])
    .map((source) => captureSourceLabel(source));
  for (const observation of trace.sourceObservations || []) {
    if (!observation?.browserFamily) continue;
    const family = browserFamilyLabel(observation.browserFamily);
    const mode = observation.privateBrowsing === true
      ? "private"
      : observation.privateBrowsing === false ? "normal" : "access unknown";
    const method = observation.observationKind === "reconciliation" ? "reconciliation" : "live";
    const version = observation.extensionVersion ? `, extension ${observation.extensionVersion}` : "";
    lines.push(`${family} ${mode}, ${method}${version}`);
  }
  // No client-surface line: the header's tooling fact already names the
  // surface, read from the conversation trace for a browser capture.
  lines.push(trace.moduleCapture
    ? `Module/delta capture${trace.moduleCaptureProjected ? " (projection row)" : ""}`
    : "No module used");
  if (trace.rawCapture) lines.push("Raw capture translated by the backend from the agent's OTLP export");
  return [...new Set(lines)];
}

export function CaptureInfoIcon({ trace }) {
  if (!traceCaptureSourceEnabled()) return null;
  const lines = captureLines(trace);
  return (
    <HoverTooltip
      placement="top"
      wide
      label={
        <span className="grid gap-[3px] py-[2px] text-left">
          <span className="font-medium">Capture</span>
          {lines.map((line) => (
            <span key={line} className="text-[color:var(--muted)]">{line}</span>
          ))}
        </span>
      }
    >
      <span
        tabIndex={0}
        aria-label="Capture sources"
        className="inline-flex cursor-help items-center text-[color:var(--muted-soft)] transition-colors hover:text-[color:var(--muted)]"
      >
        <svg viewBox="0 0 12 12" className="h-[11px] w-[11px]" fill="none" stroke="currentColor" strokeWidth="1.1" aria-hidden="true">
          <circle cx="6" cy="6" r="5" />
          <path d="M6 5.5v3" strokeLinecap="round" />
          <circle cx="6" cy="3.4" r="0.6" fill="currentColor" stroke="none" />
        </svg>
      </span>
    </HoverTooltip>
  );
}

// Suffix-split row whose full-conversation reconstruction failed or came back
// incomplete: every reading of this trace is over the newest messages stored
// inline, not the whole thread. Rendered by BOTH detail layouts — the standard
// flow and the Lanes takeover — because a reading that omits it passes a
// fragment off as the complete conversation (in Lanes, a two-turn timeline
// with zero tool calls that looks confidently final).
export function PartialBodyNotice({ trace }) {
  if (!trace?.partialBody && !trace?.requestContextRetired) return null;
  const partial = trace?.partialBody ? (trace.partialBodyReason === "partial"
    ? "The oldest messages are unavailable. Showing the recoverable portion."
    : "The full conversation could not be loaded. Showing the latest messages.") : "";
  const text = [
    trace?.requestContextRetired
      ? "Exact instructions and tool definitions for this model call are no longer retained. Conversation messages and call settings remain available."
      : "",
    partial,
  ].filter(Boolean).join(" ");
  return (
    <div
      className="rounded-[10px] border border-[color:var(--warning,#b8860b)] bg-[color:var(--control-bg)] px-[10px] py-[6px] text-[11px] leading-[1.3] text-[color:var(--muted)]"
      role="status"
    >
      {text}
    </div>
  );
}

function LoadEarlierButton({ onClick, label }) {
  return (
    <button
      type="button"
      data-load-earlier=""
      onClick={onClick}
      className="cursor-pointer rounded-[6px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[7px] py-[1px] text-[12px] leading-[1.4] text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
    >
      {label}
    </button>
  );
}

// The axis's own account of the conversation record it shows (see
// conversationRead.js): the whole conversation for a trace opened on a
// session, the conversation up to the request for one opened on a request,
// both read newest first in pages. One short sentence per state, with the control that reads the page
// before the oldest shown. Rendered wherever PartialBodyNotice is.
export function RecordAxisNotice({ trace, onLoadEarlier = null }) {
  const record = trace?.record;
  if (!record?.session) return null;
  const quiet = "rounded-[10px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[10px] py-[6px] text-[11px] leading-[1.3] text-[color:var(--muted-soft)]";
  const plain = "rounded-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[6px] text-[11px] leading-[1.3] text-[color:var(--muted)]";
  // The axis IS the record, read newest first in pages. This is the axis's
  // own account -- how much of the conversation is on it -- and the one
  // control that extends it. A page read that failed keeps what was shown and
  // offers the same control as a retry.
  const shown = Number(record.loaded) || 0;
  const known = record.total != null && Number.isFinite(Number(record.total)) ? Number(record.total) : null;
  if (record.pending) {
    return <div className={quiet} role="status" data-record-state="pending">Loading the conversation.</div>;
  }
  if (record.rejected && !shown) {
    return (
      <div className={`${plain} flex flex-wrap items-baseline gap-x-[10px] gap-y-[4px]`} role="status" data-record-state="rejected">
        <span>The conversation could not be loaded.</span>
        {onLoadEarlier ? <LoadEarlierButton onClick={onLoadEarlier} label="Retry" /> : null}
      </div>
    );
  }
  const count = known != null && known > shown
    ? `Showing the ${shown} most recent of ${known} messages.`
    : `Showing all ${shown} message${shown === 1 ? "" : "s"}.`;
  return (
    <div className={`${plain} flex flex-wrap items-baseline gap-x-[10px] gap-y-[4px]`} role="status" data-record-state={record.loading ? "loading" : "loaded"}>
      <span>{count}</span>
      {record.loading ? <span className="text-[color:var(--muted-soft)]">Loading earlier messages.</span> : null}
      {!record.loading && record.rejected ? <span className="text-[color:var(--muted-soft)]">Earlier messages could not be loaded.</span> : null}
      {!record.loading && !record.exhausted && onLoadEarlier
        ? <LoadEarlierButton onClick={onLoadEarlier} label={record.rejected ? "Retry earlier" : "Load earlier"} />
        : null}
    </div>
  );
}

// MergedMetaBlock — "everything except the prompt" as a single
// definition list: the identity breadcrumb, the session's cluster mix,
// model + token accounting, and tools called. Used by the trace-detail
// panel; capture-source detail lives behind the (i) next to the section
// title (CaptureInfoIcon), not a row here.
export function MergedMetaBlock({ trace, turns = null, onScopeNavigate }) {
  const hasIdentity = trace.sessionId || trace.endpointId || trace.primaryClusterId || trace.clusterStatus;
  const hasTools = trace.tools?.length > 0;
  // Tools actually CALLED across this context window, with counts — the
  // offered list (trace.tools) is near-identical for every trace from the
  // same harness, so the calls are what discriminate. Counts cover the whole
  // conversation the request carries, not just the newest turn.
  const calledTools = useMemo(() => {
    const counts = new Map();
    for (const turn of turns || []) {
      for (const b of turn?.blocks || []) {
        if (b?.type === "tool_use" && b.name) counts.set(b.name, (counts.get(b.name) || 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]));
  }, [turns]);
  const [showOffered, setShowOffered] = useState(false);
  // The disclosure is per-trace state: paging to another trace in the same
  // session doesn't remount this block, so an open "offered" list would
  // otherwise follow the reader to the next trace.
  useEffect(() => { setShowOffered(false); }, [trace?.id]);
  // Resolve the endpoint's friendly name from the label cache, subscribing
  // so the chip re-renders when the name lands instead of flashing the raw
  // id. Falls back to the trace's short label (then the id) on a cache miss
  // — same precedence as withEndpointLabel() in TraceLogPanel.
  const resolvedEndpoint = useLabel(trace.endpointId ? { dim: "endpoints", id: trace.endpointId } : null);
  const endpointLabel = (resolvedEndpoint && resolvedEndpoint !== trace.endpointId)
    ? resolvedEndpoint
    : (trace.endpointName || trace.endpointId);
  // Effort + fast-mode read off the request body (apiClient adaptTrace →
  // requestControls), inline at the head of the token row so the request's
  // shape and its cost read as one block. Plain text, not chips — this rail
  // keeps chips for the identity breadcrumb alone, so the pivots stand out
  // instead of everything competing in boxes. Raw wire values.
  const modelLine = (trace.model || trace.requestEffort || trace.requestSpeed) ? (
    <div className="flex flex-wrap items-baseline gap-x-[6px] text-[12px] leading-[1.3] min-w-0">
      {trace.model && (
        <button
          type="button"
          onClick={() => onScopeNavigate("models", trace.model)}
          className="max-w-full truncate text-left text-[color:var(--ink)] hover:text-[color:var(--green)] cursor-pointer transition-colors"
        >
          {trace.model}
        </button>
      )}
      {(trace.requestEffort || trace.requestSpeed) && (
        <span className="text-[color:var(--muted)]">
          {[trace.requestEffort && `${trace.requestEffort} effort`, trace.requestSpeed]
            .filter(Boolean)
            .join(" · ")}
        </span>
      )}
    </div>
  ) : null;
  return (
    <div className="grid gap-[8px] min-w-0">
      {/* The rail is where a reader looks for the trace's facts; the header
          byline carries the date too, but a rail that named the machine and
          the model and not the day read as missing one (found live). */}
      {trace.timestamp ? (
        <MetaRow label="When">
          <span className="text-[12px] leading-[1.3] text-[color:var(--ink)]">{fullTimestampFormatter.format(trace.timestamp)}</span>
        </MetaRow>
      ) : null}
      {hasIdentity && (
        <MetaRow label="Identity">
          <IdentityBreadcrumb trace={trace} endpointLabel={endpointLabel} onScopeNavigate={onScopeNavigate} />
        </MetaRow>
      )}
      {overdueAnswerCount(trace.unansweredPrompts) > 0 && (
        <MetaRow label="Answer">
          <AnswerGapMarker prompts={trace.unansweredPrompts} />
        </MetaRow>
      )}
      {/* Sourced from the TOKEN store (METRIC_TYPE_TOKEN_SUM_* via the
          EDGE_TYPE_PROMPT_HAS_TOKENS grain-aware collapse, O-2021) — the tokens
          at the session's FINEST available grain. The label + tooltip are
          driven by which grain answered (trace.tokenGrain): "turn" is THIS
          prompt's own cost (summable), while submission/session/user_day are
          shared rollups an analyst must not sum N× across the turn/session/day.
          See tokenGrainMeta() in src/lib/tokenGrain.js (imported at the top).

          Absence surfaces as null, never a silent 0: a side with no token
          observation (trace.inputTokens / .outputTokens === null) renders "—",
          and when BOTH sides are null the whole row collapses to "—" with an
          "unmeasured, not zero" tooltip — distinct from a measured 0. */}
      {(() => {
        const tokenMeta = tokenGrainMeta(trace.tokenGrain);
        const hasIn = trace.inputTokens != null;
        const hasOut = trace.outputTokens != null;
        // No token observations attributed to this prompt yet → "—" (unmeasured),
        // not "0 in / 0 out" (which would read as a measured zero).
        if (!hasIn && !hasOut) {
          return (
            <MetaRow label={tokenMeta.label}>
              <div className="grid gap-[4px] min-w-0">
                {modelLine}
                <HoverTooltip placement="top" wide label="Token usage for this prompt is unmeasured.">
                  <span tabIndex={0} className="cursor-help justify-self-start">—</span>
                </HoverTooltip>
              </div>
            </MetaRow>
          );
        }
        // At least one side present. A null SIDE shows "—" (not 0) so partial
        // measurements stay honest (e.g. "100 in / — out").
        //
        // CAVEAT this view makes legible: at submission/session grain these are
        // a TURN ROLLUP (every API call the agent made for this prompt), not one
        // request — and `input` is NON-CACHED input only (Anthropic's disjoint
        // accounting). So a heavily-cached turn reads as a tiny "in" next to a
        // huge context that's almost all cache_read. We show BOTH: the total
        // prompt-side tokens processed ("context") with how much was served from
        // cache, and the genuinely-new in/out underneath. reasoning is a SUBSET
        // of output (OpenAI/DeepSeek only), annotated, never added to a total.
        const hasReasoning = trace.reasoningTokens != null && trace.reasoningTokens > 0;
        const cacheRead = trace.cacheReadTokens || 0;
        const cacheCreation = trace.cacheCreationTokens || 0;
        // Cache is disjoint + a meaningful signal only where the provider keeps
        // it outside input (Anthropic). A measured 0 IS the signal (caching off
        // / broken — context all uncached, 0% cached); only an unmeasured cache
        // or a non-applicable provider is hidden, so "0 read" never misreads as
        // broken when it's simply not tracked for that provider.
        const cacheApplies = providerCacheApplies(trace.provider)
          && (trace.cacheReadTokens != null || trace.cacheCreationTokens != null);
        // CONTEXT + CACHE REUSE via the shared definitions (tokenVolume.js) so
        // the chip's "% cached" matches the summary-bar card and legend exactly — one
        // definition, not three. context = fresh input + cache reads/writes
        // (output is the other axis); reuse = reads ÷ context (0% = broken-cache).
        const cacheParts = { input: trace.inputTokens, cacheRead: trace.cacheReadTokens, cacheCreation: trace.cacheCreationTokens };
        const context = contextOf(cacheParts);
        const showContext = cacheApplies && context > 0;
        const reuse = cacheReuseRate(cacheParts);
        // Only claim a "% cached" when input is MEASURED. If input is null
        // (unmeasured), contextOf coerced it to 0, so the denominator omits the
        // real uncached input and the reuse rate would overstate (e.g. read 100%
        // of a context that's actually larger). Showing context without the %
        // keeps the unmeasured-≠-zero honesty the rest of the chip preserves.
        const cachedPct = (trace.inputTokens != null && reuse != null) ? Math.round(reuse * 100) : null;
        // Calls the rollup spans — the verification surface (1.9M ÷ N ≈ per-call
        // context). Only meaningful when the grain aggregated more than one call.
        const callCount = trace.callsTotal != null && trace.callsTotal > 1 ? trace.callsTotal : null;
        // Coverage: surface only when partial (measured < covered) — the case
        // where the number is incomplete and shouldn't be trusted flat.
        const partialCoverage = trace.callsTotal != null && trace.callsWithTokens != null
          && trace.callsTotal > 0 && trace.callsWithTokens < trace.callsTotal;
        const inOut = `${hasIn ? formatCompact(trace.inputTokens) : "—"} in / ${hasOut ? formatCompact(trace.outputTokens) : "—"} out`;
        const reasoningTag = hasReasoning ? ` (${formatCompact(trace.reasoningTokens)} reasoning)` : "";
        // Estimated USD cost for this trace, priced from the bundled pi-ai
        // catalog at (provider, model). null when the model isn't catalogued
        // — show nothing rather than a fake $0. Carries the same grain caveat
        // as the token numbers above (a shared turn/session rollup isn't
        // summable across prompts), so it sits under the same labelled row.
        const cost = estimateTokenCost(
          {
            input: trace.inputTokens,
            output: trace.outputTokens,
            cacheRead: trace.cacheReadTokens,
            cacheCreation: trace.cacheCreationTokens,
            inclusiveCacheRead: trace.inclusiveCacheReadTokens,
          },
          { provider: trace.provider, model: trace.model },
        );
        return (
          <MetaRow label={tokenMeta.label}>
            <div className="grid gap-[2px] min-w-0">
              {modelLine && <div className="pb-[2px]">{modelLine}</div>}
              <HoverTooltip placement="top" wide label={tokenMeta.title}>
                {/* justify-self-start: this span is a grid item, so the grid's
                    default justify-items:stretch would blow it out to the full
                    column width — and HoverTooltip anchors to the trigger's
                    rect, leaving the tooltip floating off to the right of the
                    left-aligned text. Hug the content so it anchors to the text. */}
                <span tabIndex={0} className="cursor-help justify-self-start">
                  {showContext ? (
                    <>
                      {`context ${formatCompact(context)}`}
                      {cachedPct != null && <span className="text-[color:var(--muted)]">{` · ${cachedPct}% cached`}</span>}
                    </>
                  ) : (
                    <>
                      {inOut}
                      {reasoningTag && <span className="text-[color:var(--muted)]">{reasoningTag}</span>}
                    </>
                  )}
                </span>
              </HoverTooltip>
              {showContext && (
                <span className="text-[11px] leading-[1.25] text-[color:var(--muted)]">
                  {`new ${inOut}${reasoningTag}`}
                </span>
              )}
              {showContext && (
                <span className="text-[11px] leading-[1.25] text-[color:var(--muted)]">
                  {`cache ${formatCompact(cacheRead)} read · ${formatCompact(cacheCreation)} write`}
                  {callCount != null ? ` · ${formatCompact(callCount)} calls` : ""}
                </span>
              )}
              {partialCoverage && (
                <span className="text-[11px] leading-[1.25] text-[color:var(--muted)]">
                  {`measured ${formatCompact(trace.callsWithTokens)}/${formatCompact(trace.callsTotal)} calls`}
                </span>
              )}
              {cost && (
                <span className="text-[11px] leading-[1.25] text-[color:var(--muted)]">
                  {`≈ ${formatUsd(cost.total)} est.`}
                </span>
              )}
            </div>
          </MetaRow>
        );
      })()}
      {/* Called tools as running text with counts — a pill per tool boxed
          the whole row and competed with the identity chips above. */}
      {(hasTools || calledTools.length > 0) && (
        <MetaRow label="Tools">
          <div className="grid gap-[3px] min-w-0">
            {calledTools.length > 0 ? (
              <div className="text-[12px] leading-[1.5] min-w-0">
                {/* The separator lives OUTSIDE the nowrap span: nowrap around
                    "name ×N" keeps a count with its tool, while the spaces in
                    the separator give the line its only break opportunities —
                    inside the nowrap they don't, and the row overflows the
                    panel as one unbreakable run. */}
                {calledTools.map(([name, count], i) => (
                  <Fragment key={name}>
                    {i > 0 && <span className="text-[color:var(--muted-soft)]">{" · "}</span>}
                    <span className="whitespace-nowrap">
                      <span className="text-[color:var(--ink)]">{name}</span>
                      {count > 1 && <span className="text-[color:var(--muted)]">{` ×${count}`}</span>}
                    </span>
                  </Fragment>
                ))}
              </div>
            ) : hasTools ? (
              <span className="text-[12px] leading-[1.5] text-[color:var(--muted)] min-w-0">
                {trace.tools.join(" · ")}
              </span>
            ) : null}
            {hasTools && calledTools.length > 0 && (
              <div className="grid gap-[3px] min-w-0">
                <button
                  type="button"
                  onClick={() => setShowOffered((v) => !v)}
                  className="justify-self-start text-[12px] leading-[1.2] text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors cursor-pointer"
                >
                  <span aria-hidden="true">{showOffered ? "▾" : "▸"}</span> {trace.tools.length} offered
                </button>
                {showOffered && (
                  <span className="text-[11px] leading-[1.5] text-[color:var(--muted)] min-w-0">
                    {trace.tools.join(" · ")}
                  </span>
                )}
              </div>
            )}
          </div>
        </MetaRow>
      )}
    </div>
  );
}

// ToolExecutionsBlock — the actual tool calls the agent ran during this
// turn (office-agent traces; resolved via the turn-key join in getTrace).
// Distinct from the "Tools" meta row, which lists tools *offered* in the
// api_request. Each row expands to the tool's input / output payloads.
function ToolExecutionRow({ exec }) {
  const [open, setOpen] = useState(false);
  const hasBody = Boolean(exec.input || exec.result);
  return (
    <div className="rounded-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] min-w-0">
      <button
        type="button"
        onClick={() => hasBody && setOpen((v) => !v)}
        className={[
          "flex w-full items-center gap-[6px] px-[8px] py-[4px] text-[11px] leading-[1.3] text-left",
          hasBody ? "cursor-pointer" : "cursor-default",
        ].join(" ")}
        title={exec.isError ? "Tool call returned an error" : "Tool call succeeded"}
      >
        <span className={exec.isError ? "text-[color:var(--red,#c0392b)]" : "text-[color:var(--green)]"} aria-hidden="true">
          {exec.isError ? "✕" : "✓"}
        </span>
        <span className="font-medium text-[color:var(--ink)] truncate">{exec.name}</span>
        <span className="ml-auto flex items-center gap-[6px] text-[10px] text-[color:var(--muted)] whitespace-nowrap">
          {/* An execution row IS a record that was read, so an absent
              tool_timestamp on it genuinely is a missing time, not an
              unasked question. */}
          <TraceStamp stamp={toolStamp(exec.timestamp)} />
          {hasBody && (
            <span aria-hidden="true">{open ? "▾" : "▸"}</span>
          )}
        </span>
      </button>
      {open && hasBody && (
        <div className="grid gap-[6px] border-t border-[color:var(--line-soft)] px-[8px] py-[6px] min-w-0">
          {exec.input && (
            <div className="grid gap-[2px] min-w-0">
              <span className="text-[12px] text-[color:var(--muted)]">
                Input{exec.inputTruncated ? " (truncated)" : ""}
              </span>
              <pre className="m-0 max-h-[160px] overflow-auto whitespace-pre-wrap break-all text-[11px] leading-[1.32] text-[color:var(--ink)]">{exec.input}</pre>
            </div>
          )}
          {exec.result && (
            <div className="grid gap-[2px] min-w-0">
              <span className="text-[12px] text-[color:var(--muted)]">
                Output{exec.resultTruncated ? " (truncated)" : ""}
              </span>
              <pre className="m-0 max-h-[160px] overflow-auto whitespace-pre-wrap break-all text-[11px] leading-[1.32] text-[color:var(--ink)]">{exec.result}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ToolExecutionsBlock({ executions }) {
  return (
    <div className="grid gap-[8px] border-l border-[color:var(--line-soft)] pl-[12px] py-[2px] min-w-0">
      <span className={CONTEXT_EYEBROW_CLASS}>Tool executions ({executions.length})</span>
      <div className="grid gap-[4px] min-w-0">
        {executions.map((exec) => (
          <ToolExecutionRow key={exec.id} exec={exec} />
        ))}
      </div>
    </div>
  );
}

// ── Spans (OTel-style waterfall) ─────────────────────────────────────
//
// A trace today carries no per-span timing — turns are parsed structurally
// from the api_request body, and the only timestamp anywhere is the single
// recorded `tool_time` on office-agent tool executions (no duration). So
// this view is an *honest* span tree, not a latency flame graph: spans are
// laid out in document order and bar length encodes relative content size
// (log-compressed so a 50k-char system prompt doesn't crush the tool calls
// into invisible slivers — linear or even √ leave them sub-pixel), never
// elapsed time. Parents (root → turn → block)
// span the full extent of their children, the way a real waterfall nests.
// When real OTLP span timings get exposed (O-1769 follow-up), swap the
// sequence/size layout in buildTraceSpanRows for a time axis; the row
// rendering stays as-is.

// -- Find in trace (Context reading) --

const SEARCH_DEBOUNCE_MS = 150;
// About a second and a half for a far message to mount and show its mark.
const SEARCH_LANDING_FRAMES = 90;

// Hit coordinates index the blocks as TurnBlock renders them.
const renderedBlocks = (turn) => collapseDocumentRefs(turn?.blocks || []);

function flashMark(mark) {
  mark.classList.remove("signal-mark-flash");
  void mark.offsetWidth; // restart the animation on a repeat landing
  mark.classList.add("signal-mark-flash");
  window.setTimeout(() => mark.classList.remove("signal-mark-flash"), 1600);
}

function RequestDetailContent({ trace, onScopeNavigate, onOpenTrace, pinnedFrom = null, scopePath = [], onTraceInfluence = null, autoScrollSignals = false, signalFocus = null, readingView = null, onReadingViewChange = null, onLoadEarlier = null, turnFocusRequest = null }) {
  // Trace-detail UI state lives in the URL so a deep link reproduces
  // exactly what the operator was looking at — including whether they
  // were reading the parsed turns or the raw JSON, and the chronology
  // direction. Defaults ("turns" / "latest") drop out so a fresh URL
  // stays clean.
  const [searchParams, setSearchParams] = useSearchParams();
  const historyView = readingView ?? (searchParams.get("hist") || "turns");
  const headerSlot = useDetailHeaderSlot();
  const historyOrder = searchParams.get("ord") || "latest";
  // Read fresh from the URL so writes done via writeUrlParam
  // (dcol/xcol in SplitPage / ContentShell) aren't clobbered.
  // See lib/urlParams.js + App.jsx#mergeUrlParams for the rationale.
  const updateUrlState = (key, value, defaultValue) => {
    setSearchParams(() => {
      const next = readUrlParams();
      if (value && value !== defaultValue) next.set(key, value);
      else next.delete(key);
      return next;
    }, { replace: true });
  };
  const setHistoryView  = (v) => onReadingViewChange ? onReadingViewChange(v) : updateUrlState("hist", v, "turns");
  const setHistoryOrder = (v) => updateUrlState("ord",  v, "latest");

  // ── Harness x-ray — dim the lines of this request that match
  // scaffolding mined from the scope's recent prompts. URL-backed
  // (`hx`) like the other per-trace view state, so a deep link
  // reproduces the overlay. The mining corpus comes from
  // loadHarnessProfile (sampled + cached per scope), NOT from this
  // trace alone — one payload can't tell harness from human.
  const overlayOn = searchParams.get("hx") === "1";
  const setOverlayOn = (v) => updateUrlState("hx", v ? "1" : "", "");
  const scopeKey = encodeScopePath(scopePath) || "org";
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);
  useEffect(() => {
    if (!overlayOn) return undefined;
    let on = true;
    setProfile(null);
    setProfileError(null);
    loadHarnessProfile({ scopePath }).then(
      (p) => { if (on) setProfile(p); },
      (e) => { if (on) setProfileError(e?.message || String(e)); },
    );
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayOn, scopeKey]);

  const overlayValue = useMemo(
    () => (overlayOn && profile?.boilerKeys?.size ? { boilerKeys: profile.boilerKeys } : null),
    [overlayOn, profile],
  );
  // Every reading's stamps open the capture their time came from. The capture
  // being read is the one the threads panel marks as this trace.
  const stampCapture = useMemo(
    () => (onOpenTrace ? { open: onOpenTrace, current: trace.traceId || trace.latestTraceId || trace.id } : null),
    [onOpenTrace, trace.traceId, trace.latestTraceId, trace.id],
  );
  // This request's own scaffolding share — the number the operator
  // reads before trusting the dimming.
  const harnessShare = useMemo(() => {
    if (!overlayValue || !trace?.turns?.length) return null;
    let dim = 0;
    let total = 0;
    for (const t of trace.turns) {
      if (t.role !== "system" && t.role !== "user") continue;
      for (const b of t.blocks || []) {
        const text = b.type === "text"
          ? (b.text || "")
          : b.type === "tool_result"
            ? toolResultText(b.content)
            : "";
        if (!text) continue;
        for (const seg of classifyHarnessSegments(text, overlayValue.boilerKeys)) {
          total += seg.text.length;
          if (seg.boiler) dim += seg.text.length;
        }
      }
    }
    return total > 0 ? Math.round((100 * dim) / total) : null;
  }, [overlayValue, trace]);

  // Per-trace signal-highlight locs (see useTraceSignalHighlights): every
  // detection's spans come from persisted, digest-bound evidence; only the
  // client-only efficiency signal still runs in the browser. Re-run per trace.
  const { highlights: firedHere, evidenceStatus } = useTraceSignalHighlights(trace?.sessionTranscript ? null : trace);
  const focusedFired = useMemo(() => {
    if (!signalFocus?.tag) return firedHere || [];
    return (firedHere || []).filter((entry) => String(entry?.type || "").split(".").pop() === signalFocus.tag);
  }, [firedHere, signalFocus?.tag]);

  // ── The turn coordinate shared by every reading ───────────────────
  // One range per trace: written as the reader scrolls (ANY scroll, not just
  // the signal auto-scroll), read on arrival in whichever reading they switch
  // to. Context / X-ray / Lanes / Tree all index the same trace.turns, so the
  // coordinate is meaningful in all four; Raw has no turn structure and is
  // exempt. Wire form + range semantics live in lib/traceTurnFocus.js.
  //
  // It lives in the URL (so a turn is shareable) but NOT through
  // useSearchParams: a scroll-driven param would re-render every consumer on
  // every scroll frame. writeUrlParam is the existing replaceState escape
  // hatch for exactly this (see lib/urlParams.js). The consequence is that the
  // param is deliberately non-reactive — each reading samples it when it
  // arrives, which is also the behaviour we want (apply on arrival, never
  // continuously, or capture and apply fight each other).
  // The switcher is sticky at the top of the panel viewport, and a reading can
  // have sticky bars of its own (the X-ray feed's coverage line, its episode
  // headers). Publishing the measured height as a CSS custom property lets
  // those offset below it instead of landing on top of it at `top: 0`.
  const switcherRef = useRef(null);
  const [switcherH, setSwitcherH] = useState(0);
  useEffect(() => {
    const el = switcherRef.current;
    if (!el) return undefined;
    const measure = () => setSwitcherH((prev) => {
      const h = Math.round(el.getBoundingClientRect().height);
      return prev === h ? prev : h;
    });
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // Mount-only: the observer is what catches the height changing when the
    // reading swaps the controls on the bar's right-hand side.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const turnFocusRef = useRef(parseTurnFocus(readUrlParam(TURN_FOCUS_PARAM)));
  // A link that names a MESSAGE rather than an index. It becomes a turn once
  // the loaded turns carry that message, which on a paged record may take a
  // page or two; until then there is no coordinate. Any deliberate act
  // supersedes it.
  const pendingFocusMessageRef = useRef(parseTurnFocusMessage(readUrlParam(TURN_FOCUS_PARAM)));
  const turnsRef = useRef([]);
  const [turnFocusArrival, setTurnFocusArrival] = useState(0);
  // Set by a deliberate act in any reading — expanding a turn card, picking a
  // lane cell, opening a turn in the dock. Never derived from scroll position:
  // see lib/traceTurnFocus.js for why that was dropped.
  const [focusedTurn, setFocusedTurn] = useState(() => turnFocusRef.current);
  // null deselects: every gesture that sets the coordinate is a toggle, so
  // repeating it (collapsing the card you expanded, closing the lane dock you
  // opened) takes the coordinate away again. formatTurnFocus(null) is null,
  // which writeUrlParam spells as deleting the param.
  const captureTurnFocus = useCallback((turn) => {
    const next = Number.isInteger(turn) ? turn : null;
    pendingFocusMessageRef.current = null;
    if (next === turnFocusRef.current) return;
    turnFocusRef.current = next;
    setFocusedTurn(next);
    writeUrlParam(TURN_FOCUS_PARAM, formatTurnFocus(next, turnsRef.current[next]?.messageId ?? null));
  }, []);

  // The finding a notification's link named, sampled once on arrival like the
  // turn above it. Only Lanes acts on it, because that is the reading that
  // draws the findings; it is dropped when another conversation opens, below.
  const findingFocusRef = useRef(parseFindingFocus(readUrlParam(FINDING_FOCUS_PARAM)));
  const [findingFocus, setFindingFocus] = useState(() => findingFocusRef.current);
  const clearFindingFocus = useCallback(() => {
    if (!findingFocusRef.current) return;
    findingFocusRef.current = null;
    setFindingFocus(null);
    writeUrlParam(FINDING_FOCUS_PARAM, null);
  }, []);

  // The chat whose knowledge graph the trace readings wear, and a claim of it
  // to arrive on (lib/traceGraphOverlay.js). Both are read reactively: the chat
  // hands a trace over by writing them, often into a panel already open on
  // another trace. The graph stays on while the reader walks from trace to
  // trace; the claim is an arrival, spent once Lanes lands on it.
  const graphKey = parseGraphFocus(searchParams.get(GRAPH_FOCUS_PARAM));
  const graphClaimFocus = graphKey ? parseGraphClaimFocus(searchParams.get(GRAPH_CLAIM_PARAM)) : null;
  const spendGraphClaim = useCallback(() => updateUrlState(GRAPH_CLAIM_PARAM, "", ""), []); // eslint-disable-line react-hooks/exhaustive-deps
  const closeGraph = useCallback(() => {
    setSearchParams(() => {
      const next = readUrlParams();
      next.delete(GRAPH_FOCUS_PARAM);
      next.delete(GRAPH_CLAIM_PARAM);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const openGraphChat = useCallback(() => activateGraphChat(graphKey), [graphKey]);
  // A reply typed on a claim's card, handed to the chat that owns the graph;
  // its progress comes back through graphReplies, so the reader stays on the
  // trace.
  const replyToGraph = useCallback((reply) => sendGraphReply(graphKey, reply), [graphKey]);

  // A turn index means nothing in a different conversation, so opening another
  // trace drops it rather than aiming every reading at the same number. A
  // finding id is narrower still -- it names a row about THIS conversation.
  const focusTraceRef = useRef(trace.id);
  useEffect(() => {
    if (focusTraceRef.current === trace.id) return;
    focusTraceRef.current = trace.id;
    captureTurnFocus(null);
    clearFindingFocus();
  }, [trace.id, captureTurnFocus, clearFindingFocus]);
  // Bumping the arrival seq is what re-runs the landing effects. Every door
  // into a reading goes through here so no door can forget.
  const arriveAtReading = useCallback((value) => {
    setHistoryView(value);
    setTurnFocusArrival((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!turnFocusRequest) return;
    captureTurnFocus(turnFocusRequest.turn);
    setTurnFocusArrival((n) => n + 1);
  }, [turnFocusRequest, captureTurnFocus]);

  // Pivoting into the X-ray reading carries the reader's turn range; a card's
  // turn chip carries them back (pendingTurnJumpRef → glide the turn list).
  const pendingTurnJumpRef = useRef(null);

  // Verified persisted-review citations (BackendTraceReviewPanel to onEvidence),
  // merged into the same highlight surface as fired signals so evidence
  // spans get in-text marks + locator-pill stops. The panel clears this
  // on trace change and unmount, so no reset effect is needed here.
  const [evalFired, setEvalFired] = useState(null);

  // Hand-off follow-through: the lanes dock's "open session" stages the shared
  // needle strings under the counterpart's trace id, and the scan effect below
  // (after jumpToTurn) consumes them once this trace's turns hydrate. Kept with
  // the trace id the scan ran against, so a panel re-rendered onto another
  // trace never wears the previous trace's marks.
  const [joinFired, setJoinFired] = useState(null);

  // Find-in-trace, in the Context reading. `searchInput` is what is typed;
  // `searchTerm` is it applied, after a short pause or on Enter.
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchKinds, setSearchKinds] = useState([]);
  const [activeHitId, setActiveHitId] = useState(null);
  const [searchLandSeq, setSearchLandSeq] = useState(0);
  const pendingSearchLandRef = useRef(null);
  const searchLandingRef = useRef(null);
  const searchInputRef = useRef(null);
  useEffect(() => {
    if (searchInput === searchTerm) return undefined;
    const t = window.setTimeout(() => setSearchTerm(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput, searchTerm]);
  useEffect(() => () => searchLandingRef.current?.(), []);
  const clearSearch = useCallback(() => {
    searchLandingRef.current?.();
    searchLandingRef.current = null;
    pendingSearchLandRef.current = null;
    setSearchInput("");
    setSearchTerm("");
    setSearchKinds([]);
    setActiveHitId(null);
  }, []);
  const searchTraceRef = useRef(trace?.id);
  useEffect(() => {
    if (searchTraceRef.current === trace?.id) return;
    searchTraceRef.current = trace?.id;
    clearSearch();
  }, [trace?.id, clearSearch]);

  if (!trace) {
    return (
      <div className="grid gap-[4px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px]">
        <strong className="text-[14px] leading-[1.08] font-medium">No request selected</strong>
        <span className={CONTEXT_META_CLASS}>Pick a trace from the log to inspect turns, parameters, and linked scope.</span>
      </div>
    );
  }

  // The offered tool DEFINITIONS render as blocks appended to the leading system
  // turn — the model's "setup" sits together (system prompt + the tools it was
  // handed). Appending to the END of an existing turn's blocks keeps every other
  // (turn, block) index stable, so the security signals' loc mapping is unaffected;
  // the tool_def blocks pick up fresh coordinates the highlighter can key on. With
  // no system turn, a trailing setup turn is added (nothing after it to shift).
  const allTurns = useMemo(() => {
    const base = trace.turns || [];
    const defs = trace.toolDefs || [];
    if (!defs.length) return base;
    const defBlocks = defs.map((d) => ({ type: "tool_def", name: d.name, description: d.description, schema: d.schema }));
    const sysIdx = base.findIndex((t) => t?.role === "system");
    if (sysIdx >= 0) {
      const copy = base.slice();
      copy[sysIdx] = { ...copy[sysIdx], blocks: [...(copy[sysIdx].blocks || []), ...defBlocks] };
      return copy;
    }
    return [...base, { role: "system", label: "Tools offered", blocks: defBlocks }];
  }, [trace.turns, trace.toolDefs]);
  turnsRef.current = allTurns;
  const priorTurns = useRef(allTurns);
  useEffect(() => {
    const prior = priorTurns.current;
    priorTurns.current = allTurns;
    const wanted = pendingFocusMessageRef.current;
    if (wanted) {
      const at = allTurns.findIndex((turn) => String(turn?.messageId || "").toLowerCase() === wanted);
      // Not on the axis yet: a page read later may carry it.
      if (at < 0) return;
      captureTurnFocus(at);
      setTurnFocusArrival((n) => n + 1);
      return;
    }
    const messageId = prior[turnFocusRef.current]?.messageId;
    if (prior === allTurns || !messageId) return;
    const next = allTurns.findIndex((turn) => turn.messageId === messageId);
    captureTurnFocus(next < 0 ? null : next);
  }, [allTurns, captureTurnFocus]);
  // A linked message behind the loaded page: read the page before it, one at a
  // time, until the effect above finds it. Declared after it so a page that
  // carries the message is resolved before another is asked for.
  const record = trace?.record;
  // The page asked for is on its way until the loaded count moves.
  const landingPagesRef = useRef({ pages: 0, askedAt: null });
  useEffect(() => {
    const wanted = pendingFocusMessageRef.current;
    const asked = landingPagesRef.current;
    if (!wanted || !onLoadEarlier || asked.askedAt === record?.loaded) return;
    if (!landingMayReadEarlier(record, asked.pages)) return;
    if (allTurns.some((turn) => String(turn?.messageId || "").toLowerCase() === wanted)) return;
    landingPagesRef.current = { pages: asked.pages + 1, askedAt: record.loaded };
    onLoadEarlier();
  }, [allTurns, record, onLoadEarlier]);
  const turnStamps = useMemo(() => buildTurnStamps(allTurns), [allTurns]);
  const toolStamps = useMemo(() => toolStampsByUseId(trace.toolExecutions), [trace.toolExecutions]);
  const answerGaps = useMemo(
    () => answerGapsByEvent(trace.unansweredPrompts, trace.turns),
    [trace.unansweredPrompts, trace.turns],
  );
  // The X-ray and Lanes readings get the code-mode unwrap: virtual inner
  // blocks spliced in after each Codex cell so the extractors can claim the
  // work the cell wraps. The unwrap inserts blocks WITHIN turns (turn indices
  // stay aligned with the Context reading; block indices shift), so the
  // unwrapped array must be the one those views both build from and read back
  // — and the Context reading keeps the un-inserted record.
  // The unwrap reads each cell's JS with the grammar, and there is no second
  // reader: a trace whose content depended on how recently the tab was opened
  // would be worse than a brief wait. The memo re-runs when the grammar lands;
  // a conversation with no code-mode cells never waits for one. A load that
  // FAILED is latched for the session (parserHost), so waiting on it would be
  // an eternal spinner: the cells render wrapped, behind a quiet notice.
  const grammarStatus = useGrammarWarm("javascript");
  const xrayTurns = useMemo(() => unwrapCodeModeTurns(allTurns), [allTurns, grammarStatus]);
  const xrayReadable = useMemo(() => codeModeReadable(allTurns), [allTurns, grammarStatus]);
  const xrayPending = !xrayReadable && grammarStatus !== "failed";
  const xrayFailed = !xrayReadable && grammarStatus === "failed";
  const searchResult = useMemo(
    () => searchTurns(allTurns, { term: searchTerm, kinds: searchKinds, blocksOf: renderedBlocks }),
    [allTurns, searchTerm, searchKinds],
  );
  const searchKindCounts = useMemo(() => kindCounts(allTurns, renderedBlocks), [allTurns]);
  const searchHits = useMemo(
    () => (searchResult ? hitsInDisplayOrder(searchResult.hits, historyOrder) : []),
    [searchResult, historyOrder],
  );
  const orderedHistory = historyOrder === "latest" ? [...allTurns].reverse() : allTurns;
  // A message-type filter narrows the list itself; a keyword then searches
  // what is left. With no type chosen the list is every turn, and a display row
  // is the turn index (mirrored when latest first).
  const typeRows = useMemo(() => {
    const kept = turnsOfKinds(allTurns, searchKinds, renderedBlocks);
    return kept && historyOrder === "latest" ? kept.reverse() : kept;
  }, [allTurns, searchKinds, historyOrder]);
  const typeRowAt = useMemo(() => (typeRows ? new Map(typeRows.map((realIndex, row) => [realIndex, row])) : null), [typeRows]);
  const realIndexOf = (row) => (typeRows ? typeRows[row] ?? null : historyOrder === "latest" ? allTurns.length - 1 - row : row);
  const displayIndexOf = (realIndex) => (typeRowAt ? typeRowAt.get(realIndex) ?? null : historyOrder === "latest" ? allTurns.length - 1 - realIndex : realIndex);
  const listedTurns = typeRows ? typeRows.map((realIndex) => allTurns[realIndex]) : orderedHistory;
  // Map this trace's fired signals (from the on-demand scan above) onto their
  // rendered blocks so each block can mark exactly where it fired. A search
  // replaces them while it is on: an overlapping signal span would merge into
  // a search mark and take the mark the search has to land on.
  const joinFollow = joinFired?.forTrace === trace.id ? joinFired : null;
  const highlightMap = useMemo(
    () => (searchResult
      ? searchHighlightMap(searchResult.hits)
      : buildHighlightMap(allTurns, focusedFired.concat(evalFired || []).concat(joinFollow?.entries || []))),
    [searchResult, allTurns, focusedFired, evalFired, joinFollow],
  );
  const alertTurns = useMemo(() => {
    if (!signalFocus) return [];
    return [...new Set(focusedFired.flatMap((entry) => entry?.value?.locs || []).map((loc) => loc.turn).filter(Number.isInteger))].sort((a, b) => a - b);
  }, [focusedFired, signalFocus]);
  // One navigable stop per turn that fired a signal (a turn can carry several
  // matched spans across its blocks; we jump to the turn and flash all of
  // them). Ordered by turn index; each keeps the strongest namespace's accent
  // so the locator pill reads in the right color. Drives SignalLocator below.
  const signalHits = useMemo(() => {
    const rank = (t) => (t === "risk" ? 3 : t === "pii" ? 2 : t === "audit" ? 1 : 0);
    const byTurn = new Map();
    for (const [k, arr] of highlightMap) {
      const ti = parseInt(k.split(":")[0], 10);
      if (!Number.isInteger(ti)) continue;
      for (const m of arr) {
        const ns = typeof m.type === "string" ? m.type.split(".")[0] : "";
        const prev = byTurn.get(ti);
        if (prev == null || rank(ns) > rank(prev)) byTurn.set(ti, ns);
      }
    }
    return [...byTurn.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([turnIndex, ns]) => ({ turnIndex, ns, accent: markAccent(`${ns}.x`) }));
  }, [highlightMap]);
  const virtualListRef = useRef(null);
  // Adapter-contributed trace facets. Every registered DimAdapter
  // gets a chance to surface chips on the detail panel by parsing
  // the loaded trace (see extractAllTraceFacets in dimAdapters.js).
  // Today: mcpServerAdapter emits one chip per distinct MCP server in
  // trace.tools. Future adapters add chips by implementing
  // extractTraceFacets — no edits here required.
  const traceFacetGroups = useMemo(() => extractAllTraceFacets(trace), [trace]);
  // Raw content is available only for local captures that supplied it.
  const effectiveHistoryView = effectiveTraceReading(trace, historyView);

  // Sub-agent thread hops for the Lanes dock: sibling traces joined on the
  // turn's submission id, opened through the panel's own trace navigation.
  // Memoized so the dock's fetch effect keys on a stable object, not a
  // per-render literal. Absent submission id (older captures) leaves the
  // lanes readable and only the thread listing dark.
  // Spawn prompts this conversation issued (Agent/Task tool inputs) - the
  // exact evidence the threads panel matches sibling openings against to
  // claim "spawned by this trace". Extraction and the query wiring live in
  // traceThreadsWiring.js where they are unit-tested.
  const spawnPrompts = useMemo(() => extractSpawnPrompts(allTurns), [allTurns]);

  const lanesThreads = useMemo(
    () => buildLanesThreads({
      // A request's threads are its run's calls; the whole conversation's are
      // every run's, so a sub-agent spawned from any turn on the axis can be
      // joined. The wiring takes the run when there is one.
      submissionId: trace.submissionId || null,
      sessionId: trace.sessionId || null,
      currentTraceId: trace.traceId || trace.latestTraceId || trace.id,
      toolExecutions: trace.toolExecutions,
      spawnPrompts,
      onOpenTrace,
      queryFn: query,
      // For the cross-submission sub-agent join: a spawn from another run's
      // turn is invisible to the submission-scoped sibling fetch, and the
      // endpoint pond needs to know whose captures to search.
      endpointId: trace.endpointId ?? null,
    }),
    [trace.submissionId, trace.sessionId, trace.traceId, trace.latestTraceId, trace.toolExecutions, trace.id, trace.endpointId, onOpenTrace, spawnPrompts],
  );

  // The turn at the top of the Context viewport right now — captured when the
  // reader pivots into the X-ray reading so the feed opens where they were.
  // The DOM read stays here; the geometry is topVisibleIndex (pure, so it can
  // be tested with synthetic rects — jsdom has no layout).
  const readTopVisibleTurn = () => {
    const els = [...document.querySelectorAll("[data-display-index]")];
    if (!els.length) return null;
    const viewport = findScrollParent(els[0]);
    const di = topVisibleIndex(
      els.map((el) => ({
        bottom: el.getBoundingClientRect().bottom,
        index: Number(el.dataset.displayIndex),
      })),
      viewport ? viewport.getBoundingClientRect().top : 0,
    );
    if (di == null) return null;
    return realIndexOf(di);
  };
  // The seq makes the landing effect below re-run even when the view is
  // ALREADY "turns" (evaluation citation chips jump from within the turn
  // list; the X-ray path always arrives via a view flip, which used to be
  // the only trigger).
  const [turnJumpSeq, setTurnJumpSeq] = useState(0);
  const jumpToTurn = (realIndex) => {
    // An evaluation citation or an X-ray turn chip naming a turn is a
    // deliberate "this one", so it sets the shared coordinate as well as
    // landing the turn list on it.
    captureTurnFocus(realIndex);
    pendingTurnJumpRef.current = realIndex;
    // A turn the type filter hides can only be landed on with the filter off.
    if (typeRowAt && !typeRowAt.has(realIndex)) setSearchKinds([]);
    setHistoryView("turns");
    setTurnJumpSeq((n) => n + 1);
  };
  // Land the Context list on the turn jumped to from an X-ray card. The
  // VirtualList mounts only after the view flips, and its height estimates
  // refine as rows mount — so re-aim across a few frames (SignalLocator's
  // glide trick, without the ease).
  useEffect(() => {
    if (effectiveHistoryView !== "turns" || pendingTurnJumpRef.current == null) return undefined;
    const realIndex = pendingTurnJumpRef.current;
    pendingTurnJumpRef.current = null;
    const displayIdx = displayIndexOf(realIndex);
    if (displayIdx == null) return undefined;
    let raf = 0;
    let frames = 0;
    const aim = () => {
      const top = virtualListRef.current?.topForIndex?.(displayIdx);
      const rowEl = document.querySelector("[data-display-index]");
      const viewport = rowEl ? findScrollParent(rowEl) : null;
      if (top != null && viewport) {
        viewport.scrollTop = Math.max(0, Math.min(top - viewport.clientHeight * 0.38, viewport.scrollHeight - viewport.clientHeight));
      }
      if (frames++ < 8) raf = window.requestAnimationFrame(aim);
    };
    raf = window.requestAnimationFrame(aim);
    return () => window.cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveHistoryView, historyOrder, allTurns.length, turnJumpSeq]);


  // Apply: land the turn list on the carried range when the reader arrives
  // here from another reading. Once per arrival — re-applying while they
  // scroll would fight the capture above. `first` is the top of the range, so
  // landing is "put first at the top" and the rest of the range follows.
  // -1, not 0: arrival 0 is the COLD MOUNT, which is a real arrival — a deep
  // link carrying ?turn=356 must land. Seeding this at 0 would mark that
  // first arrival as already serviced and silently ignore the link.
  const appliedArrivalRef = useRef(-1);
  useEffect(() => {
    if (effectiveHistoryView !== "turns" || !allTurns.length) return undefined;
    if (appliedArrivalRef.current === turnFocusArrival) return undefined;
    // Consume the arrival BEFORE testing the coordinate. An arrival with no
    // turn to land on is still serviced -- otherwise the first turn captured
    // while already reading here (expanding a card, the signal locator's own
    // glide) would be mistaken for a landing and yank the list out from under
    // the reader, or fight the glide that set it. Restored by the cleanup so a
    // StrictMode re-run re-lands rather than skipping.
    const serviced = appliedArrivalRef.current;
    appliedArrivalRef.current = turnFocusArrival;
    let started = false;
    const focus = clampTurnFocus(focusedTurn, allTurns.length);
    // `== null`, not falsy: turn 0 is a real turn.
    if (focus == null) return undefined;
    const displayIdx = displayIndexOf(focus);
    if (displayIdx == null) {
      // The type filter hides this turn: drop the filter and land on the re-run.
      if (typeRows) {
        appliedArrivalRef.current = serviced;
        setSearchKinds([]);
      }
      return undefined;
    }
    let raf = 0;
    let frames = 0;
    // Re-aim across a few frames: the VirtualList's height estimates refine as
    // rows mount, so the first answer is not the final one.
    const aim = () => {
      started = true;
      const top = virtualListRef.current?.topForIndex?.(displayIdx);
      const rowEl = document.querySelector("[data-display-index]");
      const viewport = rowEl ? findScrollParent(rowEl) : null;
      if (top != null && viewport) {
        const goal = top - stickyLandingInset();
        viewport.scrollTop = Math.max(0, Math.min(goal, viewport.scrollHeight - viewport.clientHeight));
      }
      if (frames++ < 8) raf = window.requestAnimationFrame(aim);
    };
    raf = window.requestAnimationFrame(aim);
    return () => {
      window.cancelAnimationFrame(raf);
      if (!started) appliedArrivalRef.current = serviced;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveHistoryView, historyOrder, allTurns.length, turnFocusArrival, focusedTurn, typeRows]);

  // Changing reading must not cost the position. The switcher is sticky, so
  // "where you were" is the switcher at the top of the viewport — but leaving
  // the Lanes takeover restores the metadata/anatomy sections above it, which
  // drops the reader at the top of the panel with the switcher far below.
  // Park it back at the top on arrival. Skipped when a turn is carried: the
  // turn landings below aim lower, and two scrolls in one commit would fight.
  const parkArrivalRef = useRef(0);
  useEffect(() => {
    if (parkArrivalRef.current === turnFocusArrival) return undefined;
    const serviced = parkArrivalRef.current;
    parkArrivalRef.current = turnFocusArrival;
    let started = false;
    if (clampTurnFocus(focusedTurn, allTurns.length) != null) return undefined;
    const raf = window.requestAnimationFrame(() => {
      started = true;
      const bar = switcherRef.current;
      const viewport = bar ? findScrollParent(bar) : null;
      if (!viewport) return;
      const top = viewport.scrollTop
        + (bar.getBoundingClientRect().top - viewport.getBoundingClientRect().top);
      viewport.scrollTop = Math.max(0, Math.min(top, viewport.scrollHeight - viewport.clientHeight));
    });
    return () => {
      window.cancelAnimationFrame(raf);
      if (!started) parkArrivalRef.current = serviced;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnFocusArrival, effectiveHistoryView]);

  // Consume a staged hand-off follow and land in the reader's CURRENT view:
  // the Lanes view receives the strings (followNeedles below) and rings the
  // carrying cells; the turn list gets in-text marks and jumps to the first
  // shared span, the way an evaluation citation lands on its quote. One-shot
  // by design -- the take happens only once turns exist to scan, so the
  // shallow-open render before hydration can't burn the payload. The needles
  // are kept even when the turn-list scan lands nothing: the lanes locate on
  // their own segment surface (which includes content the turn scan skips).
  useEffect(() => {
    if (!allTurns.length) return;
    const follow = takeJoinFollow(trace.id);
    if (!follow) return;
    const entries = scanTurnsForNeedles(allTurns, follow.needles);
    setJoinFired({ forTrace: trace.id, entries, needles: follow.needles });
    if (entries.length && effectiveHistoryView === "turns") {
      jumpToTurn(Math.min(...entries.flatMap((e) => e.value.locs.map((l) => l.turn))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trace.id, allTurns]);

  // Find-in-trace stepping. Landing runs after the commit that a step (or a
  // mode switch) causes, so it reads the rows that commit drew.
  const goToHit = (hitId) => {
    setActiveHitId(hitId);
    pendingSearchLandRef.current = hitId;
    setSearchLandSeq((n) => n + 1);
  };
  const stepHit = (delta) => {
    const n = searchHits.length;
    if (!n) return;
    const at = searchHits.findIndex((h) => h.id === activeHitId);
    const next = at < 0 ? (delta > 0 ? 0 : n - 1) : (at + delta + n) % n;
    goToHit(searchHits[next].id);
  };
  const toggleSearchKind = (kind) => {
    setSearchKinds((prev) => (prev.includes(kind) ? prev.filter((k) => k !== kind) : SEARCH_KINDS.filter((k) => k === kind || prev.includes(k))));
  };
  // A new term or filter lands on its first hit.
  useEffect(() => {
    const first = searchResult?.term ? searchHits[0] : null;
    if (first) goToHit(first.id);
    else setActiveHitId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult?.term, searchKinds]);
  useEffect(() => {
    const hitId = pendingSearchLandRef.current;
    if (hitId == null || effectiveHistoryView !== "turns") return;
    pendingSearchLandRef.current = null;
    const hit = searchHits.find((h) => h.id === hitId);
    const displayIdx = hit ? displayIndexOf(hit.turn) : null;
    if (displayIdx == null) return;
    searchLandingRef.current?.();
    const viewport = switcherRef.current ? findScrollParent(switcherRef.current) : null;
    const root = viewport || document;
    const findMark = () => [...root.querySelectorAll("mark[data-search-hit]")].find((m) => m.dataset.searchHit === hitId) || null;
    // The list mounts the message; the mark then centres itself.
    let releaseRow = findMark() ? null : virtualListRef.current?.scrollToIndex?.(displayIdx, { align: "center", inset: stickyLandingInset() });
    let stopMark = null;
    let raf = 0;
    let frames = 0;
    const settle = () => {
      const mark = findMark();
      if (mark) {
        releaseRow?.();
        releaseRow = null;
        stopMark = landOnRow(findMark, { align: "center" });
        flashMark(mark);
        return;
      }
      if (frames++ < SEARCH_LANDING_FRAMES) raf = window.requestAnimationFrame(settle);
    };
    raf = window.requestAnimationFrame(settle);
    searchLandingRef.current = () => {
      window.cancelAnimationFrame(raf);
      releaseRow?.();
      stopMark?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchLandSeq]);
  const clearSearchKeyword = () => {
    setSearchInput("");
    setSearchTerm("");
  };
  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (searchInput !== searchTerm) setSearchTerm(searchInput);
      else stepHit(e.shiftKey ? -1 : 1);
    } else if (e.key === "Escape" && searchInput) {
      e.preventDefault();
      e.stopPropagation();
      clearSearchKeyword();
    }
  };
  // The scope line names what a search could not see: the messages still unread.
  const searchRecord = trace.record;
  const searchLoaded = Number(searchRecord?.loaded) || 0;
  const searchKnown = searchRecord?.total != null && Number.isFinite(Number(searchRecord.total)) ? Number(searchRecord.total) : null;
  const searchPartial = searchResult != null && Boolean(searchRecord?.session) && !searchRecord.exhausted && searchKnown != null && searchKnown > searchLoaded;

  // Date + the skwach prompt summary together as the trace header: the
  // timestamp as a small muted eyebrow, the full summary (no truncation)
  // beneath it as the headline. Same gist the operator clicked on in the
  // trace log, so the panel opens with a readable title rather than a
  // separate busy section.
  // A trace whose getTrace read resolved empty is still rendered from its
  // placeholder seed, which carries no event time — formatting that as a date
  // would print the epoch under the header as though it were observed.
  // With the detail header's byline above (it carries the timestamp), the
  // block keeps the summary alone: the same date twice, five pixels apart,
  // was one of the lines that made the header read as chrome (found live).
  // The session's workspace in miniature heads the reading once its turns
  // can be read, and its card names the trace: the header says the name only
  // while the miniature is not there (and the time only off the detail
  // header's line), and is not drawn when it has nothing to say.
  const previewShown = allTurns.length > 0 && !xrayPending;
  const headerTime = !!trace.timestamp && !headerSlot;
  const headerTitle = !!trace.summary && !previewShown;
  const headerBar = headerTime || headerTitle ? (
    <div className="grid gap-[3px] border-b border-[color:var(--line-soft)] pb-[6px] min-w-0">
      {headerTime ? (
        <span className="text-[11px] leading-[1.18] text-[color:var(--muted)]">
          {fullTimestampFormatter.format(trace.timestamp)}
        </span>
      ) : null}
      {headerTitle ? (
        <strong className="text-[13px] leading-[1.3] font-medium text-[color:var(--ink)]">
          {trace.summary}
        </strong>
      ) : null}
    </div>
  ) : null;

  // Return-to-origin — same across all variants, lives at the very top.
  const returnToOrigin = pinnedFrom && !pinnedFrom.isCurrent && pinnedFrom.onNavigate ? (
    <div>
      <button
        type="button"
        onClick={pinnedFrom.onNavigate}
        title={`Return to ${pinnedFrom.label}`}
        className="inline-flex items-center gap-[4px] rounded-[6px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] py-[2px] text-[12px] leading-[1.1] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)]"
      >
        <svg viewBox="0 0 12 12" className="h-[10px] w-[10px]" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M7 2L3 6l4 4" />
          <path d="M3 6h6" />
        </svg>
        <span className="truncate max-w-[160px]">{pinnedFrom.label}</span>
      </button>
    </div>
  ) : null;

  // The session on its own canvas, over this page (TraceCanvasOverlay). The
  // record read here goes with it, so the canvas need not read it again, when
  // it is the session's own and settled: not still loading, not a failed read,
  // not a request's axis ending at the request (the canvas is the session's).
  const openOnCanvas = () => {
    const r = trace.record;
    const own = !!r?.session && !r.pending && !r.rejected && !Number.isFinite(r.untilMs);
    previewTraceCanvas({ id: trace.sessionId || trace.id, title: trace.summary || "", trace: own ? trace : null });
  };
  // The reading switcher: on the detail header's line when the header offers
  // a slot, else at the head of the reading.
  const viewSwitcherEl = (
    <ReadingSwitcherGroup
      options={allTurns.length > 0
        ? [
            ["turns", "Context"],
            ["work", "X-ray"],
            ["lanes", "Lanes"],
            ...(trace.apiRequestRaw ? [["raw", "Raw"]] : []),
          ]
        : [["raw", "Raw"]]}
      active={effectiveHistoryView}
      onSelect={arriveAtReading}
      onWorkspace={allTurns.length > 0 ? openOnCanvas : null}
    />
  );

  // What the panel puts on the detail header's line when the header offers a
  // slot: the switcher, then, on the Lanes reading alone, the trace's summary,
  // truncated. Every other reading opens with the summary as its own headline
  // block, so the line would say it twice five pixels apart there. The shell
  // tab clips the same words to a few; the header has the room for most of
  // them (found live, on the Lanes reading). It takes the line's slack and
  // gives way three times faster than the byline when the line is short: the
  // byline's facts matter more than the tail of a sentence the tab also
  // carries. Under 640px of header it stands down rather than showing a
  // one-letter stub beside the byline's date.
  const headerLineEl = (
    <>
      {viewSwitcherEl}
      {effectiveHistoryView === "lanes" && trace.summary ? (
        <span data-header-summary="" className="min-w-0 truncate text-[12px] leading-[1.2] font-medium text-[color:var(--ink)] [flex:1_3_auto] @max-[640px]:hidden">
          {trace.summary}
        </span>
      ) : null}
    </>
  );


  // Trace detail layout: header, then the session's workspace in miniature
  // (its card as the workspace draws it, which opens the workspace), the
  // metadata definition list behind its hairline left rail, related work, and
  // the conversation. The filled prompt card this panel used to open with
  // duplicated the newest turn (and the summary above it), so it's gone: the
  // newest turn opens by default in the Context list instead.
  //
  // The Lanes reading is a TAKEOVER, not a section: the timeline is the
  // panel's primary surface there, so the metadata and related-work sections
  // stand aside and the switcher moves into the header. Switching back to any
  // other reading restores the full layout.
  return (
    <HarnessOverlayContext.Provider value={overlayValue}>
    <SignalHighlightContext.Provider value={highlightMap.size ? highlightMap : null}>
    <SearchCurrentContext.Provider value={activeHitId}>
    <ToolStampContext.Provider value={toolStamps.size ? toolStamps : null}>
    <StampCaptureContext.Provider value={stampCapture}>
    {trace.sessionTranscript && joinFollow && !joinFollow.entries.length ? (
      <p role="status" className="text-[12px] leading-[1.4] text-[color:var(--muted)]">
        The stored quote was not found in the current captured trace. The source content may have changed or be unavailable.
      </p>
    ) : null}
    {effectiveHistoryView === "lanes" ? (
      <div data-lanes-takeover="" className="grid min-w-0 content-start gap-[8px]">
        {returnToOrigin}
        {headerSlot ? (
          // The header's line has the byline; the switcher and the summary join
          // it there.
          createPortal(headerLineEl, headerSlot)
        ) : (
          <div className="flex min-w-0 items-center gap-[10px] border-b border-[color:var(--line-soft)] pt-[1px] pb-[11px]">
            {/* A host without the compact header: the switcher takes a row of
                its own under the request-tab wrapper's padding, with the
                summary beside it. */}
            {viewSwitcherEl}
            {trace.summary ? (
              <span className="min-w-0 flex-1 truncate text-right text-[11px] leading-[1.2] text-[color:var(--muted)]">{trace.summary}</span>
            ) : null}
          </div>
        )}
        {!xrayPending ? (
        <>
        <TraceLanesView
          trace={trace}
          onScopeNavigate={onScopeNavigate}
          turnFocus={clampTurnFocus(focusedTurn, allTurns.length)}
          turnFocusArrival={turnFocusArrival}
          onTurnFocus={captureTurnFocus}
          findingFocus={findingFocus}
          graphKey={graphKey}
          graphClaimFocus={graphClaimFocus}
          onGraphClaimLanded={spendGraphClaim}
          onGraphClose={closeGraph}
          onOpenGraphChat={openGraphChat}
          onGraphReply={replyToGraph}
          turns={xrayTurns}
          onOpenTrace={onOpenTrace}
          // The axis's account and its caveats lead the timeline's own toolbar
          // line, beside the counts, instead of stacking as boxes above it.
          // The record's account and its paging control ride the ruler's rail
          // head; the caveats about what could be read ride that head's hover
          // with the counts. Nothing stacks above the plane.
          record={trace.record || null}
          onLoadEarlier={onLoadEarlier}
          axisNotes={[
            trace.partialBody
              ? (trace.partialBodyReason === "partial"
                ? "The oldest messages are unavailable. Showing the recoverable portion."
                : "The full conversation could not be loaded. Showing the latest messages.")
              : null,
            xrayFailed ? CODE_CELLS_AS_WRITTEN : null,
          ]}
          followNeedles={joinFollow?.needles || null}
          alertTurns={alertTurns}
          traceTimestamp={trace.timestamp}
          densityBasis={{
            totals: {
              input: trace.inputTokens,
              output: trace.outputTokens,
              cacheRead: trace.cacheReadTokens,
              cacheWrite: trace.cacheCreationTokens,
              inclusiveCacheRead: trace.inclusiveCacheReadTokens,
            },
            provider: trace.provider,
            model: trace.model,
            callsTotal: trace.callsTotal,
            // Level metadata for the strip's cost attribution: the session's
            // spend rows (per provider x model x run) are the preferred level
            // — the conversation shown IS session history, so one run's
            // rollup understates it badly — with grain naming what `totals`
            // means when the strip has to fall back to it. sessionRows is the
            // stable array from getTrace (identity feeds the strip's memo).
            grain: trace.tokenGrain,
            sessionRows: trace.sessionTokens,
            currentSubmissionId: trace.submissionId,
          }}
          threads={lanesThreads}
          fill
        />
        </>
        ) : (
          <CodeModePending />
        )}
      </div>
    ) : (
    <div className="grid gap-[10px] min-w-0">
      {returnToOrigin}
      {headerBar}
      {traceFacetGroups.map(({ adapterId, groupLabel, facets }) => (
        <div key={adapterId} className="grid gap-[4px] min-w-0">
          <span className={CONTEXT_EYEBROW_CLASS}>{groupLabel}</span>
          <div className="flex flex-wrap gap-[4px] min-w-0">
            {facets.map(({ type, label, count, tooltip }) => (
              <span
                key={type}
                title={tooltip}
                className="inline-flex items-baseline gap-[5px] rounded-[5px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] py-[2px] text-[11px] leading-[1.08] text-[color:var(--ink)]"
              >
                <span>{label}</span>
                {count > 1 && (
                  <span className="text-[10px] text-[color:var(--muted)]">×{count}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      ))}
      {/* The session's workspace in miniature: its card as the workspace
          draws it, which opens the workspace. It reads the unwrapped turns,
          as X-ray and Lanes do, once the grammar is up. */}
      {previewShown ? (
        <WorkspaceCardPreview trace={trace} turns={xrayTurns} onOpen={openOnCanvas} />
      ) : null}
      {/* The saved backend review of this trace, as a disclosure tab. */}
      {!trace.sessionTranscript ? <TraceOverlays
        trace={trace}
        onJumpToTurn={jumpToTurn}
        onEvidence={setEvalFired}
      /> : null}

      <div className="grid gap-[8px] border-l border-[color:var(--line-soft)] pl-[12px] py-[2px] min-w-0">
        <div className="flex items-center gap-[6px]">
          <span className={CONTEXT_EYEBROW_CLASS}>Trace metadata</span>
          <CaptureInfoIcon trace={trace} />
        </div>
        <MergedMetaBlock trace={trace} turns={allTurns} onScopeNavigate={onScopeNavigate} />
      </div>

      {/* Prior sessions sharing file anchors with this one, and the census's
          answer to the complementary question (which tenant-wide initiatives
          this session was part of), sharing one tabbed section. */}
      <WorkTabs trace={trace} onOpenTrace={onOpenTrace} />

      {/* What the agent actually ran this turn (office-agent traces).
          Distinct from the offered-tools meta row above. */}
      {trace.toolExecutions?.length > 0 && (
        <ToolExecutionsBlock executions={trace.toolExecutions} />
      )}

      {/* Cross-link facets render inline next to the turn that produced
          them — see aggregateTurnChips() above. The trace-level
          aggregation that used to live here was just noise. */}

      <PartialBodyNotice trace={trace} />
      <RecordAxisNotice trace={trace} onLoadEarlier={onLoadEarlier} />

      {/* Context · X-ray · Lanes · Raw — the readings of the same trace: the
          turn-by-turn history, the work feed, the timeline, and the original
          api_request payload. Shown whenever there are turns OR a raw
          payload (non-standard shapes parse to zero turns but still have a
          payload to read or download). */}
      {(allTurns.length > 0 || trace.apiRequestRaw) && (
        <div
          className="flex flex-col gap-[8px] pt-[2px] min-w-0"
          style={{ "--reading-switcher-h": `${switcherH}px` }}
        >
          {/* Sticky, not absolute, for the same reason as the locator pill and
              the evidence notice below: this panel's scroll viewport is itself
              positioned (OverlayScrollbars), so an absolutely-positioned child
              is laid out against the viewport and then scrolls with the
              content. Sticky is what keeps the switcher reachable from deep in
              a long conversation — changing reading should not cost the
              position you are trying to carry. Opaque background so turn cards
              cannot show through; z-index below the locator pill. */}
          <div
            ref={switcherRef}
            data-reading-switcher=""
            data-context-reading-selector=""
            className="sticky top-0 z-[5] -mx-[1px] grid gap-[8px] border-b border-[color:var(--line-soft)] bg-[color:var(--bg)] px-[1px] py-[11px]"
          >
            <div className="flex items-center justify-between gap-[8px]">
              {/* The switcher itself joins the detail header's line when the header
                  offers a slot; the bar keeps the reading's own controls. */}
              {headerSlot ? createPortal(headerLineEl, headerSlot) : viewSwitcherEl}
              {effectiveHistoryView === "turns" ? (
                <div className="flex items-center gap-[10px]">
                  <HoverTooltip
                    wide
                    placement="top"
                    label={
                      <span className="grid gap-[6px] py-[2px]">
                        <span className="font-medium">Harness traffic</span>
                        <span className="text-[color:var(--muted)]">
                          Agent harnesses such as Claude Code, Cursor, and Copilot assemble
                          model input from system prompts, reminders, tool results, and task
                          notifications. A person may write only part of a request.
                        </span>
                        <span className="text-[color:var(--muted)]">
                          USER identifies the input role in the model protocol. Tool output
                          and scheduled notifications can also use this role.
                        </span>
                        <span className="text-[color:var(--muted)]">
                          The overlay dims recurring lines and slot-filled patterns such as
                          IDs, paths, and quoted payloads. Brighter text is more likely to be
                          original user or model content.
                        </span>
                      </span>
                    }
                  >
                    <button
                      type="button"
                      onClick={() => setOverlayOn(!overlayOn)}
                      className={[
                        "text-[12px] leading-[1.08] transition-colors cursor-pointer",
                        overlayOn
                          ? "text-[color:var(--green)] font-medium"
                          : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                      ].join(" ")}
                    >
                      Harness
                    </button>
                  </HoverTooltip>
                  <span className="text-[11px] leading-[1.08] text-[color:var(--line-strong)]">|</span>
                  <button
                    type="button"
                    onClick={() => setHistoryOrder(historyOrder === "latest" ? "earliest" : "latest")}
                    className="text-[12px] leading-[1.08] text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors cursor-pointer"
                  >
                    {historyOrder === "latest" ? "Latest first" : "Earliest first"}
                  </button>
                </div>
              ) : effectiveHistoryView === "raw" ? (
                <button
                  type="button"
                  onClick={() => downloadRawJson(trace)}
                  className="text-[12px] leading-[1.08] text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors cursor-pointer"
                >
                  Download JSON
                </button>
              ) : null}
            </div>
            {effectiveHistoryView === "turns" ? (
              <TraceSearchBar
                inputRef={searchInputRef}
                input={searchInput}
                onInput={setSearchInput}
                onKeyDown={onSearchKeyDown}
                onClearKeyword={clearSearchKeyword}
                result={searchResult}
                hits={searchHits}
                activeHitId={activeHitId}
                onStep={stepHit}
                kinds={searchKinds}
                counts={searchKindCounts}
                onToggleKind={toggleSearchKind}
                onClearKinds={() => setSearchKinds([])}
              />
            ) : null}
            {effectiveHistoryView === "turns" && searchPartial ? (
              <div data-search-scope="" aria-busy={searchRecord.loading ? "true" : undefined} className="flex flex-wrap items-baseline gap-x-[10px] gap-y-[4px] text-[11px] leading-[1.3] text-[color:var(--muted)]">
                <span>Searching the {searchLoaded} most recent of {searchKnown} messages.</span>
                {searchRecord.loading
                  ? <span className="text-[color:var(--muted-soft)]">Loading earlier messages.</span>
                  : onLoadEarlier ? <LoadEarlierButton onClick={onLoadEarlier} label="Load earlier" /> : null}
              </div>
            ) : null}
          </div>
          {overlayOn && effectiveHistoryView === "turns" ? (
            <span
              className={[
                "text-[10px] leading-[1.4] text-[color:var(--muted)]",
                !profile && !profileError ? "animate-pulse" : "",
              ].join(" ")}
            >
              {profileError
                ? `Could not analyze harness traffic: ${profileError}`
                : !profile
                  ? "Analyzing recent prompts for harness patterns…"
                  : profile.templateCount === 0
                    ? profile.sessionCount < profile.threshold
                      ? `More evidence is needed. ${profile.sessionCount} sampled trace${profile.sessionCount === 1 ? " has" : "s have"} no recurring or slot-filled lines.`
                      : `No recurring harness patterns found across ${profile.sessionCount} sampled traces.`
                    : `Dimmed lines match recurring or slot-filled patterns. ${profile.templateCount} blocks from ${profile.promptCount} prompts across ${profile.sessionCount} traces${harnessShare != null ? ` · about ${harnessShare}% of this request` : ""}.`}
            </span>
          ) : null}
          {effectiveHistoryView === "turns" ? (
            typeRows && !typeRows.length ? (
              <div data-search-empty="" className="rounded-[10px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[10px] py-[8px] text-[11px] leading-[1.3] text-[color:var(--muted)]">
                No loaded messages of this type.
              </div>
            ) : (
            <CaptureSourceContext.Provider value={trace.promptCaptureSource || ""}>
            <VirtualList
              ref={virtualListRef}
              items={listedTurns}
              anchorPrepend
              // A turn that carries its message id is keyed by it, so a page
              // prepended to a session axis (or a record landing in front of a
              // capture) shifts indices without remounting the rows already
              // open; an index key is the fallback for turns without one.
              getKey={(turn, index) => (turn.messageId
                ? `${trace.id}-m-${turn.messageId}`
                : `${trace.id}-${turn.label}-${realIndexOf(index)}`)}
              renderRow={(turn, index) => {
                // Map the displayed row index back to the turn's position
                // in trace.turns so the influence agent labels it correctly
                // regardless of ordering or a type filter.
                const realIndex = realIndexOf(index);
                // The newest turn opens by default — it is the prompt this
                // trace exists for, and the standalone card that used to show
                // it above the metadata is gone. On a conversation-record axis
                // the turns this capture shipped open too, so the call being
                // read is never folded away among its history.
                return <TurnBlock turn={turn} turnIndex={realIndex} stamp={turnStamps[realIndex]} defaultOpen={realIndex === allTurns.length - 1 || Boolean(turn.fromThisCapture)} onScopeNavigate={onScopeNavigate} onTraceInfluence={onTraceInfluence} focused={realIndex === focusedTurn} onFocusTurn={captureTurnFocus} answerGap={answerGapForTurn(turn, answerGaps)} kinds={searchKinds} />;
              }}
              estRowHeight={140}
              className="min-w-0"
              rowClassName="pb-[4px] min-w-0"
            />
            </CaptureSourceContext.Provider>
            )
          ) : effectiveHistoryView === "work" ? (
            xrayPending ? (
              <CodeModePending />
            ) : (
            <>
            {xrayFailed ? <CodeModeUnavailable /> : null}
            <TraceWorkFeed
              turns={xrayTurns}
              turnFocus={clampTurnFocus(focusedTurn, allTurns.length)}
              turnFocusArrival={turnFocusArrival}
              onJumpToTurn={jumpToTurn}
              traceTimestamp={trace.timestamp}
              sessionId={trace.sessionId}
              traceId={trace.id}
              endpointId={trace.endpointId}
            />
            </>
            )
          ) : (
            <RawApiRequestView trace={trace} />
          )}
        </div>
      )}
      {/* Pinned to the panel root (not the turn list) so it stays at the
          viewport bottom wherever you've scrolled — a locator buried inside
          the below-the-fold turn history only appears once you reach it,
          which defeats the point. Turns view only: marks render there. */}
      {effectiveHistoryView === "turns" && signalHits.length > 0 && !searchResult && !typeRows && (
        <SignalLocator
          hits={signalHits}
          order={historyOrder}
          totalTurns={allTurns.length}
          listRef={virtualListRef}
          autoKey={`${trace.id}:${historyOrder}:${signalHits.length}`}
          autoScroll={autoScrollSignals}
          onFocusTurn={captureTurnFocus}
        />
      )}
      {/* Sticky, not absolute, for the same reason as the locator above: this
          panel's scroll viewport is itself positioned (OverlayScrollbars), so
          an absolutely-positioned child is laid out against the viewport and
          then scrolled with the content — it only sits at the bottom edge at
          scroll top, and rides up over the metadata everywhere else. */}
      {effectiveHistoryView === "turns" && ["partial", "unavailable"].includes(evidenceStatus) && (
        <div data-signal-evidence-notice="" className="pointer-events-none sticky bottom-[10px] z-[3] -mt-[6px] flex justify-start pl-[1px]">
          {/* Opaque --bg, not the 5.5%-alpha --control-bg: this floats over
              scrolling metadata rows, and a near-transparent fill lets their
              text read straight through the notice. Same treatment as the
              locator pill. */}
          <div className="rounded-[7px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[5px] text-[11px] text-[color:var(--muted)] shadow-[0_2px_10px_rgba(0,0,0,0.28)]">
            {evidenceStatus === "partial"
              ? "Some signal highlights are unavailable for this request."
              : "Signal highlights are unavailable for this request."}
          </div>
        </div>
      )}
    </div>
    )}
    </StampCaptureContext.Provider>
    </ToolStampContext.Provider>
    </SearchCurrentContext.Provider>
    </SignalHighlightContext.Provider>
    </HarnessOverlayContext.Provider>
  );
}

export { CONTEXT_TAB_OPTIONS };

const ContextPanel = memo(function ContextPanel({
  selectedTrace,
  // Role-aware denial copy when the raw getTrace read was refused
  // (aggregate-only role). When set, the request tab renders this message
  // instead of the trace detail or the loading placeholder — so a denied
  // read never sits forever on a fake "Loading trace".
  traceError = null,
  activeTab,
  loadingTrace = false,
  onTabChange,
  onScopeNavigate,
  onOpenTrace,
  onScopeNavigatePath,
  baseContextId,
  hideHeader = false,
  scopePath = [],
  pinnedFrom = null,
  onTraceInfluence = null,
  autoScrollSignals = false,
  signalFocus = null,
  readingView = null,
  onReadingViewChange = null,
  // The axis's "earlier" control (ScopedPage reads the page before the
  // oldest turn on the axis). Stable identity: it resolves the active tab at
  // call time, so it is not part of the memo comparison below.
  onLoadEarlier = null,
  turnFocusRequest = null,
}) {
  const contextScrollHostRef = useRef(null);
  const scrollPositionsRef = useRef(new Map());
  const lastContextKeyRef = useRef("");
  const lastScrollTopRef = useRef(0);
  const effectiveTab = (selectedTrace || loadingTrace || traceError) && activeTab === "request" ? "request" : "attribution";
  const contextContentClassName = effectiveTab === "attribution"
    ? "grid min-h-full gap-0 pr-[2px]"
    : "grid gap-[8px] pr-[2px]";
  const activeContextKey = selectedTrace
    ? `trace:${selectedTrace.id}:${effectiveTab}${loadingTrace ? ":loading" : ""}`
    : (traceError && effectiveTab === "request")
      ? `trace:error:${effectiveTab}`
      : `${baseContextId}:attribution`;
  const activeTitle = effectiveTab === "request"
    ? (selectedTrace ? selectedTrace.summary : "Request")
    : "Contrast";

  useEffect(() => {
    const viewport = getOverlayViewport(contextScrollHostRef.current);
    if (!viewport) return undefined;
    const syncScrollTop = () => {
      lastScrollTopRef.current = viewport.scrollTop;
      scrollPositionsRef.current.set(activeContextKey, viewport.scrollTop);
    };
    syncScrollTop();
    viewport.addEventListener("scroll", syncScrollTop, { passive: true });
    return () => viewport.removeEventListener("scroll", syncScrollTop);
  }, [activeContextKey]);

  useEffect(() => {
    const viewport = getOverlayViewport(contextScrollHostRef.current);
    if (!viewport) return undefined;
    const previousKey = lastContextKeyRef.current;
    if (previousKey && previousKey !== activeContextKey) {
      scrollPositionsRef.current.set(previousKey, lastScrollTopRef.current);
    }
    const nextScrollTop = scrollPositionsRef.current.get(activeContextKey) || 0;
    const frameId = window.requestAnimationFrame(() => {
      viewport.scrollTop = nextScrollTop;
      lastScrollTopRef.current = nextScrollTop;
      lastContextKeyRef.current = activeContextKey;
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [activeContextKey]);

  const isAttribution = effectiveTab === "attribution";

  const headerBlock = hideHeader ? null : (
    <div data-context-header className="overflow-hidden border-b border-[color:var(--line-soft)]">
      <div className="grid gap-[6px] pt-3 pb-2">
        <div>
          <div className={CONTEXT_EYEBROW_CLASS}>Context</div>
          <h2 className="m-0 mt-[3px] text-[15px] leading-[1.06] font-medium tracking-[-0.04em]">{activeTitle}</h2>
        </div>
        <ControlGroup className="mt-[2px]" items={CONTEXT_TAB_OPTIONS} activeValue={effectiveTab} onSelect={onTabChange} />
      </div>
    </div>
  );

  const hasHeader = Boolean(headerBlock);

  if (isAttribution) {
    return (
      <div
        className="grid h-full min-h-0 min-w-0 overflow-hidden bg-[color:var(--bg)]"
        style={{ gridTemplateRows: hasHeader ? "auto minmax(0,1fr)" : "minmax(0,1fr)" }}
      >
        {headerBlock}
        <div className="min-h-0 overflow-hidden">
          <ContrastPanel scopePath={scopePath} onNavigate={onScopeNavigate} onNavigatePath={onScopeNavigatePath} />
        </div>
      </div>
    );
  }

  return (
    <div className="grid h-full min-h-0 min-w-0 overflow-hidden bg-[color:var(--bg)]">
      <ScrollArea data-context-scroll hostRef={contextScrollHostRef} className="min-h-0 h-full" contentClassName="flex min-h-full flex-col">
        {headerBlock}
        {/* Six above, the same air the compact header keeps around its own line,
            so the header, the reading's first row and the plane sit at one
            rhythm; ten read as a gap the toolbar had fallen into (found live). */}
        <div className={["grid pt-[6px] pb-[14px]", contextContentClassName].join(" ")}>
          {effectiveTab === "request"
            ? traceError
              ? (
                  <div role="alert" className="px-[2px] py-[8px] text-[12px] leading-[1.5] text-[color:var(--muted)]">
                    {traceError}
                  </div>
                )
              : loadingTrace
                ? <TraceDetailSkeleton trace={selectedTrace} pinnedFrom={pinnedFrom} />
                : <RequestDetailContent trace={selectedTrace} onScopeNavigate={onScopeNavigate} onOpenTrace={onOpenTrace} pinnedFrom={pinnedFrom} scopePath={scopePath} onTraceInfluence={onTraceInfluence} autoScrollSignals={autoScrollSignals} signalFocus={signalFocus} readingView={readingView} onReadingViewChange={onReadingViewChange} onLoadEarlier={onLoadEarlier} turnFocusRequest={turnFocusRequest} />
            : null}
        </div>
      </ScrollArea>
    </div>
  );
}, (prev, next) =>
  prev.selectedTrace === next.selectedTrace &&
  prev.readingView === next.readingView &&
  prev.turnFocusRequest === next.turnFocusRequest &&
  prev.onReadingViewChange === next.onReadingViewChange &&
  prev.traceError === next.traceError &&
  prev.activeTab === next.activeTab &&
  prev.loadingTrace === next.loadingTrace &&
  prev.baseContextId === next.baseContextId &&
  prev.scopePath === next.scopePath &&
  prev.autoScrollSignals === next.autoScrollSignals &&
  prev.signalFocus?.namespace === next.signalFocus?.namespace &&
  prev.signalFocus?.tag === next.signalFocus?.tag &&
  prev.pinnedFrom?.isCurrent === next.pinnedFrom?.isCurrent &&
  prev.pinnedFrom?.label === next.pinnedFrom?.label);

export default ContextPanel;
